import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";
import { GoogleSearchAutoComplete } from "../../../../components/FormElements/AddressFields/GoogleSearchAutoComplete/GoogleSearchAutoComplete";

/** ResMan-aligned industry list (same set as conventional LeaseApplication). */
const RESMAN_INDUSTRIES = [
  "Automotive",
  "Construction",
  "Education",
  "Finance & Insurance",
  "Government",
  "HealthCare",
  "Hospitality",
  "Manufacturing",
  "Other",
  "Public Admin",
  "Real Estate",
  "Retail",
  "Service",
  "Technology",
  "Unknown",
] as const;

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits.length ? `(${digits}` : "";
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatCityStateZip = (addr: {
  city?: string;
  state?: string;
  zip?: string;
}) => {
  const city = addr.city || "";
  const state = addr.state || "";
  const zip = addr.zip || "";
  if (city && state && zip) return `${city}, ${state} ${zip}`;
  return [city, state, zip].filter(Boolean).join(", ");
};
export type AdditionalJobKind = "wages" | "cashWork" | "selfEmployment";

export interface AdditionalJob {
  id: string;
  kind: AdditionalJobKind;
  occupation: string;
  employerName: string;
  employerAddress: string;
  employerCityStateZip: string;
  employerWorkPhone: string;
  employmentIndustry: string;
  employmentStartDate: string;
  employmentEndDate: string;
  employmentCurrentlyEmployed: boolean;
  supervisorName: string;
  supervisorPhone: string;
  amount: string;
  frequency: string;
}

const emptyAdditionalJob = (
  kind: AdditionalJobKind = "wages"
): AdditionalJob => ({
  id: `job-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  kind,
  occupation: "",
  employerName: "",
  employerAddress: "",
  employerCityStateZip: "",
  employerWorkPhone: "",
  employmentIndustry: "",
  employmentStartDate: "",
  employmentEndDate: "",
  employmentCurrentlyEmployed: true,
  supervisorName: "",
  supervisorPhone: "",
  amount: "",
  frequency: "",
});

export type MaritalStatusValue =
  | ""
  | "Single"
  | "Married"
  | "Separated"
  | "Estranged"
  | "Divorced"
  | "Widowed";

export interface HouseholdIncomeForm {
  employmentIncome: boolean;
  // Regular wage / salary income declared by the applicant. ProofUp is advisory,
  // so affordable forms cannot depend on paystub extraction to know this amount.
  employmentIncomeAmount: string;
  employmentIncomeFrequency:
    | ""
    | "weekly"
    | "bi-weekly"
    | "semi-monthly"
    | "monthly"
    | "annually";
  selfEmployment: boolean;
  cashWork: boolean;
  // Cash work is EARNED income and is quantified like employment: a per-period
  // gross amount + cadence, captured only when cashWork === true. cashWorkAmount
  // is a money string (^\d+(\.\d{1,2})?$); cashWorkFrequency reuses the
  // week/month/annually value set shared with childSupportFrequency.
  cashWorkAmount: string;
  cashWorkFrequency: string;
  // Extra jobs / side income beyond the primary wage + cash-work fields.
  // Calah (Tigoni): selecting wages AND cash work only showed one employer
  // block, with no way to add a second job. Each entry is a full employer
  // + amount/frequency so a second W-2, gig, or cash side job can be listed.
  additionalJobs: AdditionalJob[];
  unemployment: boolean;
  vaGiBill: boolean;
  militaryIncome: boolean;
  publicAssistance: boolean;
  ssRetirement: boolean;
  ssiSsdi: boolean;
  disabilityDeathBenefits: boolean;
  workersComp: boolean;
  cashAssistanceFromOthers: boolean;
  billPaymentHelp: boolean;
  trustsAnnuitiesPensions: boolean;
  interestDividends: boolean;
  realEstatePropertyIncome: boolean;
  onlineIncome: boolean;
  directSales: boolean;
  educationAssistance: boolean;
  unearnedIncomeMinors: boolean;
  publicHousingSection8: boolean;
  zeroIncomeMember: boolean;
  otherIncomeNotListed: boolean;
  otherIncomeDescription: string;
  // Per-benefit monthly amounts (Option A). Each is captured inline when the
  // matching benefit Yes/No is answered "Yes", for the items the Non-Employed
  // Cert's "unemployment compensation or other benefits" bucket covers
  // (BENEFIT_AMOUNT_KEYS). Summed at cert-generation time into the cert's
  // `unemployment_amount` (signer can still edit). Stored as money strings.
  unemploymentAmount: string;
  vaGiBillAmount: string;
  publicAssistanceAmount: string;
  ssRetirementAmount: string;
  ssiSsdiAmount: string;
  disabilityDeathBenefitsAmount: string;
  workersCompAmount: string;
  trustsAnnuitiesPensionsAmount: string;
  // Spousal Status sub-section — TDHCA marital cert inputs.
  // Only the conditional fields (spouseName onward) are populated when
  // maritalStatus is Separated or Estranged; otherwise they stay blank
  // and the cert is skipped.
  maritalStatus: MaritalStatusValue;
  spouseName: string;
  separationDate: string;
  possessLegalDocs: boolean | null;
  noLegalDocsReason: string;
  legalActionsTaken: string;
  receivingSpousalContribution: boolean | null;
  spousalContributionAmount: string;
  // Forward-looking spousal radios — Section B signals for the zero-income
  // cert. Captured inline on the Spousal Status sub-step (S/E only) rather
  // than re-asked in the next-12-months second pass.
  anticipatedSpousalContribution: boolean | null;
  awardedIncome: boolean | null;
  anticipatedAwardedIncome: boolean | null;
  estrangedConfirmed: boolean | null;
  estrangedInitials: string;
  // Child Support Certification — captured in the dedicated childSupportCert
  // sub-step (only rendered when the household roster has minors). Maps to
  // TDHCA cert form fields status_a..e, amount_a..d, freq_a/b_(week|month|annually).
  childSupportChildIds: string[];
  childSupportStatus: "" | "a" | "b" | "c" | "d" | "e";
  childSupportAmount: string;
  childSupportFrequency: "" | "week" | "month" | "annually";
  // Recurring Gift / Assistance Verification — captured inline in the
  // Support & Assistance sub-step when the resident answers Yes to either
  // cashAssistanceFromOthers or billPaymentHelp. Drives the third-party
  // gift-giver verification DocuSeal form (HUD chain-of-custody — source
  // signs in their own DocuSeal session, never tenant-couriered).
  recurringGiftSourceName: string;
  recurringGiftSourceEmail: string;
  recurringGiftSourcePhone: string;
  // Non-Employed cert Section B — "I have accepted an offer of employment
  // but have not yet started." Rendered inline below the employmentIncome
  // Yes/No on the employment sub-step, visible only when
  // employmentIncome === false. Narrower than the second-pass
  // anticipatedIncome.employmentIncome (which covers any future wage income).
  // When true the Upload Center also requires an `offerLetter` document.
  pendingJobOffer: boolean | null;
  // Cash Tip Income Certification — captured in the dedicated `cashTip`
  // sub-step, rendered only when employmentIncome === true. Drives the
  // per-adult Cash Tip cert; every employed adult signs regardless of which
  // status they pick. cashTipFrequency + cashTipAmount apply only when
  // cashTipStatus === 'unreported'; cashTipNotTippedExplanation applies only
  // when cashTipStatus === 'none'.
  occupation: string;
  // Employer details for TAA Supplemental §2 (emp_present_employer, emp_address,
  // emp_city_state_zip, emp_work_phone). Captured on the employment sub-step
  // alongside occupation; required when employmentIncome === true. employerName
  // hardens emp_present_employer, which otherwise relies solely on ProofUp.
  employerName: string;
  employerAddress: string;
  employerCityStateZip: string;
  employerWorkPhone: string;
  employmentIndustry: string;
  employmentStartDate: string;
  employmentEndDate: string;
  employmentCurrentlyEmployed: boolean;
  supervisorName: string;
  supervisorPhone: string;
  cashTipStatus: "" | "unreported" | "reported" | "none";
  cashTipFrequency:
    | ""
    | "daily"
    | "weekly"
    | "bi-weekly"
    | "semi-monthly"
    | "monthly"
    | "quarterly"
    | "annually";
  cashTipAmount: string;
  cashTipNotTippedExplanation: string;
  // Housing Choice Voucher (HCV) details — captured on a dedicated sub-step
  // that fires when (publicHousingSection8 === true) OR
  // (zeroIncomeMember === true). The zero-income gate exists because a
  // voucher is a likely undisclosed income source; explicit Yes/No on
  // `hasHcvVoucher` filters out false positives. Bedroom size + porting +
  // expiry are required only when hasHcvVoucher === true.
  hasHcvVoucher: boolean | null;
  hcvBedroomSize: "" | "0" | "1" | "2" | "3" | "4" | "5+";
  hcvIsPorting: boolean | null;
  hcvIssuingPha: string;
  hcvPortabilityExpires: string;
  // Next-12-months pass — mirror of the 21 Yes/No income items, asked only
  // when the applicant has confirmed zero CURRENT income
  // (zeroIncomeMember === true). Feeds the zero-income cert's Section B
  // (pending_new_income). Items default to null (UNSET) and yup requires
  // each one when zeroIncomeMember === true; the form auto-resets these
  // when zeroIncomeMember flips back to anything other than true.
  anticipatedIncome: {
    employmentIncome: boolean | null;
    selfEmployment: boolean | null;
    cashWork: boolean | null;
    unemployment: boolean | null;
    vaGiBill: boolean | null;
    militaryIncome: boolean | null;
    publicAssistance: boolean | null;
    ssRetirement: boolean | null;
    ssiSsdi: boolean | null;
    disabilityDeathBenefits: boolean | null;
    workersComp: boolean | null;
    cashAssistanceFromOthers: boolean | null;
    billPaymentHelp: boolean | null;
    trustsAnnuitiesPensions: boolean | null;
    interestDividends: boolean | null;
    realEstatePropertyIncome: boolean | null;
    onlineIncome: boolean | null;
    directSales: boolean | null;
    educationAssistance: boolean | null;
    unearnedIncomeMinors: boolean | null;
    publicHousingSection8: boolean | null;
    otherIncomeNotListed: boolean | null;
    otherIncomeDescription: string;
  };
  // Section C of the zero-income cert — captured ONLY when the applicant
  // has zero current AND zero anticipated income (Section A all No + Section
  // B 'none'). The cert asks for the name of the person/entity paying each
  // expense, or "N/A" if it doesn't apply. Blank values default to "N/A"
  // in the cert prefill so the field can never render empty.
  expensesPaySources: {
    rent: string;
    utilities: string;
    food: string;
    clothing: string;
    schoolSupplies: string;
    phone: string;
    tvInternet: string;
    medical: string;
    medications: string;
    personalCare: string;
    vehicle: string;
    creditCards: string;
    other: string;
    additionalComments: string;
  };
}

export interface HouseholdMinorOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface HouseholdIncomeProps {
  defaultValues?: HouseholdIncomeForm;
  onSubmit: (data: HouseholdIncomeForm) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
  // Household minors. For HoH, sourced from this resident's own
  // `applicationData.memberProfiles.members`. For co-applicants, surfaced
  // via the lease's HoH application (passed in by parent).
  householdMinors?: HouseholdMinorOption[];
}

type IncomeItemKey = Exclude<
  keyof HouseholdIncomeForm,
  | "otherIncomeDescription"
  | "unemploymentAmount"
  | "vaGiBillAmount"
  | "publicAssistanceAmount"
  | "ssRetirementAmount"
  | "ssiSsdiAmount"
  | "disabilityDeathBenefitsAmount"
  | "workersCompAmount"
  | "trustsAnnuitiesPensionsAmount"
  | "maritalStatus"
  | "spouseName"
  | "separationDate"
  | "possessLegalDocs"
  | "noLegalDocsReason"
  | "legalActionsTaken"
  | "receivingSpousalContribution"
  | "spousalContributionAmount"
  | "awardedIncome"
  | "estrangedConfirmed"
  | "estrangedInitials"
  | "childSupportChildIds"
  | "childSupportStatus"
  | "childSupportAmount"
  | "childSupportFrequency"
  | "recurringGiftSourceName"
  | "recurringGiftSourceEmail"
  | "recurringGiftSourcePhone"
  | "employmentIncomeAmount"
  | "employmentIncomeFrequency"
  | "occupation"
  | "employerName"
  | "employerAddress"
  | "employerCityStateZip"
  | "employerWorkPhone"
  | "employmentIndustry"
  | "employmentStartDate"
  | "employmentEndDate"
  | "supervisorName"
  | "supervisorPhone"
  | "cashTipStatus"
  | "cashTipFrequency"
  | "cashTipAmount"
  | "cashTipNotTippedExplanation"
  | "cashWorkAmount"
  | "cashWorkFrequency"
  | "additionalJobs"
  | "hasHcvVoucher"
  | "hcvBedroomSize"
  | "hcvIsPorting"
  | "hcvIssuingPha"
  | "hcvPortabilityExpires"
>;

type IncomeGroupKey =
  | "employment"
  | "cashTip"
  | "benefits"
  | "socialSecurity"
  | "support"
  | "other"
  | "zeroIncome"
  | "anyOther"
  | "hcv"
  | "spousalStatus"
  | "childSupportCert"
  | "employmentNext12"
  | "benefitsNext12"
  | "socialSecurityNext12"
  | "supportNext12"
  | "otherNext12"
  | "anyOtherNext12"
  | "expensesPaySources";

interface IncomeGroup {
  groupKey: IncomeGroupKey;
  Icon: React.ComponentType<{ sx?: any }>;
  items: IncomeItemKey[];
}

// True for the second-pass next-12-months groups; the renderer uses this to
// route Controller `name`s into the nested `anticipatedIncome.<item>` path
// instead of the top-level current-state field.
const isNext12 = (k: IncomeGroupKey): boolean => k.endsWith("Next12");

const GROUPS: IncomeGroup[] = [
  {
    groupKey: "spousalStatus",
    Icon: PeopleOutlineIcon,
    items: [],
  },
  {
    groupKey: "childSupportCert",
    Icon: FavoriteBorderIcon,
    items: [],
  },
  {
    groupKey: "employment",
    Icon: WorkOutlineIcon,
    items: ["employmentIncome", "selfEmployment", "cashWork"],
  },
  // Cash Tip Income Certification — captures occupation + tip-status path
  // (unreported / reported / not-tipped) + conditional frequency+amount or
  // explanation. Filtered out of effectiveGroups when employmentIncome !== true.
  {
    groupKey: "cashTip",
    Icon: AttachMoneyIcon,
    items: [],
  },
  {
    groupKey: "benefits",
    Icon: ShieldOutlinedIcon,
    items: ["unemployment", "vaGiBill", "militaryIncome", "publicAssistance"],
  },
  {
    groupKey: "socialSecurity",
    Icon: AccountBalanceOutlinedIcon,
    items: [
      "ssRetirement",
      "ssiSsdi",
      "disabilityDeathBenefits",
      "workersComp",
    ],
  },
  {
    groupKey: "support",
    Icon: FavoriteBorderIcon,
    items: ["cashAssistanceFromOthers", "billPaymentHelp"],
  },
  {
    groupKey: "other",
    Icon: AttachMoneyIcon,
    items: [
      "trustsAnnuitiesPensions",
      "interestDividends",
      "realEstatePropertyIncome",
      "onlineIncome",
      "directSales",
      "educationAssistance",
      "unearnedIncomeMinors",
      "publicHousingSection8",
    ],
  },
  // anyOther comes before zeroIncome — we ask the catch-all "any other
  // income?" first, and only fall through to zeroIncome when the applicant
  // has said No to every income item including this catch-all.
  {
    groupKey: "anyOther",
    Icon: HelpOutlineIcon,
    items: ["otherIncomeNotListed"],
  },
  {
    groupKey: "zeroIncome",
    Icon: MoneyOffIcon,
    items: ["zeroIncomeMember"],
  },
  // Housing Choice Voucher (HCV) details. Fires when the applicant said Yes
  // to publicHousingSection8 (path a) OR declared zero income (path b — a
  // voucher is a common undisclosed income source). Filtered out of
  // effectiveGroups when neither trigger applies.
  {
    groupKey: "hcv",
    Icon: AccountBalanceOutlinedIcon,
    items: [],
  },
  // Second pass — only rendered when zeroIncomeMember === true. Mirrors the
  // six current-state income groups above with the same item keys, and the
  // renderer routes Controller `name`s to `anticipatedIncome.<item>` via
  // isNext12(). Feeds the zero-income cert's Section B (pending_new_income).
  {
    groupKey: "employmentNext12",
    Icon: WorkOutlineIcon,
    items: ["employmentIncome", "selfEmployment", "cashWork"],
  },
  {
    groupKey: "benefitsNext12",
    Icon: ShieldOutlinedIcon,
    items: ["unemployment", "vaGiBill", "militaryIncome", "publicAssistance"],
  },
  {
    groupKey: "socialSecurityNext12",
    Icon: AccountBalanceOutlinedIcon,
    items: [
      "ssRetirement",
      "ssiSsdi",
      "disabilityDeathBenefits",
      "workersComp",
    ],
  },
  {
    groupKey: "supportNext12",
    Icon: FavoriteBorderIcon,
    items: ["cashAssistanceFromOthers", "billPaymentHelp"],
  },
  {
    groupKey: "otherNext12",
    Icon: AttachMoneyIcon,
    items: [
      "trustsAnnuitiesPensions",
      "interestDividends",
      "realEstatePropertyIncome",
      "onlineIncome",
      "directSales",
      "educationAssistance",
      "unearnedIncomeMinors",
      "publicHousingSection8",
    ],
  },
  {
    groupKey: "anyOtherNext12",
    Icon: HelpOutlineIcon,
    items: ["otherIncomeNotListed"],
  },
  // Zero-income cert Section C — only renders when the applicant has zero
  // CURRENT income AND zero anticipated income. Captures who pays for each
  // of 14 expense categories. Custom JSX render (no income-style items).
  {
    groupKey: "expensesPaySources",
    Icon: AttachMoneyIcon,
    items: [],
  },
];

// All income Yes/No items EXCEPT zeroIncomeMember. If any of these is Yes
// the applicant is not zero-income, so the zeroIncome screen is skipped
// and zeroIncomeMember is forced to false.
const INCOME_ITEMS_NON_ZERO: IncomeItemKey[] = [
  "employmentIncome",
  "selfEmployment",
  "cashWork",
  "unemployment",
  "vaGiBill",
  "militaryIncome",
  "publicAssistance",
  "ssRetirement",
  "ssiSsdi",
  "disabilityDeathBenefits",
  "workersComp",
  "cashAssistanceFromOthers",
  "billPaymentHelp",
  "trustsAnnuitiesPensions",
  "interestDividends",
  "realEstatePropertyIncome",
  "onlineIncome",
  "directSales",
  "educationAssistance",
  "unearnedIncomeMinors",
  "publicHousingSection8",
  "otherIncomeNotListed",
];

// Income items start unanswered so the applicant must explicitly pick Yes
// or No. The interface keeps `boolean` because the post-validation value is
// always boolean; the null cast only applies to the initial unanswered state,
// which the toggle's `=== true ? "yes" : === false ? "no" : null` check
// renders as neither button highlighted.
const UNSET = null as unknown as boolean;

// Income items the Non-Employed Cert's "unemployment compensation or other
// benefits" bucket covers. Each gets an inline monthly-amount input (Option A)
// shown only when answered "Yes" on its current-state question; the backend
// sums the checked ones into the cert's unemployment_amount. Mirrors the
// API's BENEFIT_LABELS in document-signing.ts (excludes wage-like military).
const BENEFIT_AMOUNT_KEYS = [
  "unemployment",
  "vaGiBill",
  "publicAssistance",
  "ssRetirement",
  "ssiSsdi",
  "disabilityDeathBenefits",
  "workersComp",
  "trustsAnnuitiesPensions",
] as const;

const DEFAULT_VALUES: HouseholdIncomeForm = {
  employmentIncome: UNSET,
  employmentIncomeAmount: "",
  employmentIncomeFrequency: "",
  selfEmployment: UNSET,
  cashWork: UNSET,
  cashWorkAmount: "",
  cashWorkFrequency: "",
  additionalJobs: [],
  unemployment: UNSET,
  vaGiBill: UNSET,
  militaryIncome: UNSET,
  publicAssistance: UNSET,
  ssRetirement: UNSET,
  ssiSsdi: UNSET,
  disabilityDeathBenefits: UNSET,
  workersComp: UNSET,
  cashAssistanceFromOthers: UNSET,
  billPaymentHelp: UNSET,
  trustsAnnuitiesPensions: UNSET,
  interestDividends: UNSET,
  realEstatePropertyIncome: UNSET,
  onlineIncome: UNSET,
  directSales: UNSET,
  educationAssistance: UNSET,
  unearnedIncomeMinors: UNSET,
  publicHousingSection8: UNSET,
  zeroIncomeMember: UNSET,
  otherIncomeNotListed: UNSET,
  otherIncomeDescription: "",
  unemploymentAmount: "",
  vaGiBillAmount: "",
  publicAssistanceAmount: "",
  ssRetirementAmount: "",
  ssiSsdiAmount: "",
  disabilityDeathBenefitsAmount: "",
  workersCompAmount: "",
  trustsAnnuitiesPensionsAmount: "",
  maritalStatus: "",
  spouseName: "",
  separationDate: "",
  possessLegalDocs: null,
  noLegalDocsReason: "",
  legalActionsTaken: "",
  receivingSpousalContribution: null,
  spousalContributionAmount: "",
  anticipatedSpousalContribution: null,
  awardedIncome: null,
  anticipatedAwardedIncome: null,
  estrangedConfirmed: null,
  estrangedInitials: "",
  childSupportChildIds: [],
  childSupportStatus: "",
  childSupportAmount: "",
  childSupportFrequency: "",
  recurringGiftSourceName: "",
  recurringGiftSourceEmail: "",
  recurringGiftSourcePhone: "",
  pendingJobOffer: null,
  occupation: "",
  employerName: "",
  employerAddress: "",
  employerCityStateZip: "",
  employerWorkPhone: "",
  employmentIndustry: "",
  employmentStartDate: "",
  employmentEndDate: "",
  employmentCurrentlyEmployed: true,
  supervisorName: "",
  supervisorPhone: "",
  cashTipStatus: "",
  cashTipFrequency: "",
  cashTipAmount: "",
  cashTipNotTippedExplanation: "",
  hasHcvVoucher: null,
  hcvBedroomSize: "",
  hcvIsPorting: null,
  hcvIssuingPha: "",
  hcvPortabilityExpires: "",
  anticipatedIncome: {
    employmentIncome: UNSET,
    selfEmployment: UNSET,
    cashWork: UNSET,
    unemployment: UNSET,
    vaGiBill: UNSET,
    militaryIncome: UNSET,
    publicAssistance: UNSET,
    ssRetirement: UNSET,
    ssiSsdi: UNSET,
    disabilityDeathBenefits: UNSET,
    workersComp: UNSET,
    cashAssistanceFromOthers: UNSET,
    billPaymentHelp: UNSET,
    trustsAnnuitiesPensions: UNSET,
    interestDividends: UNSET,
    realEstatePropertyIncome: UNSET,
    onlineIncome: UNSET,
    directSales: UNSET,
    educationAssistance: UNSET,
    unearnedIncomeMinors: UNSET,
    publicHousingSection8: UNSET,
    otherIncomeNotListed: UNSET,
    otherIncomeDescription: "",
  },
  expensesPaySources: {
    rent: "",
    utilities: "",
    food: "",
    clothing: "",
    schoolSupplies: "",
    phone: "",
    tvInternet: "",
    medical: "",
    medications: "",
    personalCare: "",
    vehicle: "",
    creditCards: "",
    other: "",
    additionalComments: "",
  },
};

export const HouseholdIncome: React.FC<HouseholdIncomeProps> = ({
  defaultValues,
  onSubmit,
  onBack,
  isSaving = false,
  householdMinors = [],
}) => {
  const { t } = useTranslation();

  // maritalStatus values that require the awarded-income (alimony / child
  // support / survivor benefits) Y/N questions. Divorced is included for the
  // TAA Supplemental form, which maps these to its §4 Alimony + Child-Support
  // rows — but Divorced does NOT need the separation-certification fields
  // (spouse name, separation date, legal docs, spousal contribution). Single
  // source of truth so the yup gate, the render gate, and validateGroup stay
  // in lockstep.
  const requiresAwardedIncome = (v?: string | null) =>
    v === "Separated" || v === "Estranged" || v === "Divorced";

  // Each income Yes/No must be explicitly answered by the applicant; the
  // toggle starts unselected (UNSET in DEFAULT_VALUES) and yup catches null
  // via .typeError, then .required for safety.
  const requiredYesNoMsg = t(
    "affordable.application.householdIncome.requiredYesNo"
  ) as string;
  const requiredYesNo = () =>
    yup.boolean().typeError(requiredYesNoMsg).required(requiredYesNoMsg);

  // Each benefit's monthly amount is required + money-formatted only when its
  // current-state Yes/No is "Yes"; otherwise ignored. Built here so the eight
  // near-identical rules stay in lockstep with BENEFIT_AMOUNT_KEYS.
  const benefitAmountValidations = Object.fromEntries(
    BENEFIT_AMOUNT_KEYS.map((k) => [
      `${k}Amount`,
      yup.string().when(k, {
        is: true,
        then: (s) =>
          s
            .matches(
              /^\d+(\.\d{1,2})?$/,
              t(
                "affordable.application.householdIncome.benefitAmountInvalid"
              ) as string
            )
            .required(
              t(
                "affordable.application.householdIncome.benefitAmountRequired"
              ) as string
            ),
        otherwise: (s) => s.notRequired(),
      }),
    ])
  );

  const schema = yup.object({
    ...benefitAmountValidations,
    employmentIncome: requiredYesNo(),
    selfEmployment: requiredYesNo(),
    cashWork: requiredYesNo(),
    unemployment: requiredYesNo(),
    vaGiBill: requiredYesNo(),
    militaryIncome: requiredYesNo(),
    publicAssistance: requiredYesNo(),
    ssRetirement: requiredYesNo(),
    ssiSsdi: requiredYesNo(),
    disabilityDeathBenefits: requiredYesNo(),
    workersComp: requiredYesNo(),
    cashAssistanceFromOthers: requiredYesNo(),
    billPaymentHelp: requiredYesNo(),
    trustsAnnuitiesPensions: requiredYesNo(),
    interestDividends: requiredYesNo(),
    realEstatePropertyIncome: requiredYesNo(),
    onlineIncome: requiredYesNo(),
    directSales: requiredYesNo(),
    educationAssistance: requiredYesNo(),
    unearnedIncomeMinors: requiredYesNo(),
    publicHousingSection8: requiredYesNo(),
    zeroIncomeMember: requiredYesNo(),
    otherIncomeNotListed: requiredYesNo(),
    otherIncomeDescription: yup.string().when("otherIncomeNotListed", {
      is: true,
      then: (s) =>
        s.required(
          t("affordable.application.householdIncome.describeRequired") as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
    maritalStatus: yup
      .string()
      .oneOf([
        "Single",
        "Married",
        "Separated",
        "Estranged",
        "Divorced",
        "Widowed",
      ])
      .required(
        t(
          "affordable.application.householdIncome.spousalStatus.maritalStatusRequired"
        ) as string
      ),
    spouseName: yup.string().when("maritalStatus", {
      is: (v: string) => v === "Separated" || v === "Estranged",
      then: (s) =>
        s.required(
          t(
            "affordable.application.householdIncome.spousalStatus.spouseNameRequired"
          ) as string
        ),
      otherwise: (s) => s.notRequired(),
    }),
    possessLegalDocs: yup.boolean().when("maritalStatus", {
      is: (v: string) => v === "Separated" || v === "Estranged",
      then: (s) =>
        s
          .typeError(
            t(
              "affordable.application.householdIncome.spousalStatus.possessLegalDocsRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.spousalStatus.possessLegalDocsRequired"
            ) as string
          ),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    noLegalDocsReason: yup
      .string()
      .when(["maritalStatus", "possessLegalDocs"], {
        is: (m: string, p: boolean | null) =>
          (m === "Separated" || m === "Estranged") && p === false,
        then: (s) =>
          s.required(
            t(
              "affordable.application.householdIncome.spousalStatus.noLegalDocsReasonRequired"
            ) as string
          ),
        otherwise: (s) => s.notRequired(),
      }),
    receivingSpousalContribution: yup.boolean().when("maritalStatus", {
      is: (v: string) => v === "Separated" || v === "Estranged",
      then: (s) =>
        s
          .typeError(
            t(
              "affordable.application.householdIncome.spousalStatus.receivingContributionRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.spousalStatus.receivingContributionRequired"
            ) as string
          ),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    spousalContributionAmount: yup
      .string()
      .when(["maritalStatus", "receivingSpousalContribution"], {
        is: (m: string, r: boolean | null) =>
          (m === "Separated" || m === "Estranged") && r === true,
        then: (s) =>
          s.required(
            t(
              "affordable.application.householdIncome.spousalStatus.amountRequired"
            ) as string
          ),
        otherwise: (s) => s.notRequired(),
      }),
    pendingJobOffer: yup.boolean().when("employmentIncome", {
      is: false,
      then: (s) => s.typeError(requiredYesNoMsg).required(requiredYesNoMsg),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    employmentIncomeAmount: yup.string().when("employmentIncome", {
      is: true,
      then: (s) =>
        s
          .matches(
            /^\d+(\.\d{1,2})?$/,
            t(
              "affordable.application.householdIncome.employmentIncomeAmountInvalid"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.employmentIncomeAmountRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    employmentIncomeFrequency: yup.string().when("employmentIncome", {
      is: true,
      then: (s) =>
        s
          .oneOf(
            ["weekly", "bi-weekly", "semi-monthly", "monthly", "annually"],
            t(
              "affordable.application.householdIncome.employmentIncomeFrequencyRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.employmentIncomeFrequencyRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    // Cash Tip Income Cert — only the cashTip sub-step's fields are gated
    // on employmentIncome === true. cashTipFrequency/Amount required only on
    // the 'unreported' branch; cashTipNotTippedExplanation required only on
    // the 'none' branch.
    // Occupation + employer details are shared employment info: required when
    // the adult declared employment income OR cash work (cash work is treated
    // like employment, so it needs an employer of record too).
    occupation: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.occupationRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    employerName: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.employerNameRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    employerAddress: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.employerAddressRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    employerCityStateZip: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.employerCityStateZipRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    employerWorkPhone: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.employerWorkPhoneRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    employmentIndustry: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.industryRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    employmentStartDate: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.startDateRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    supervisorName: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.supervisorNameRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    supervisorPhone: yup.string().when(["employmentIncome", "cashWork"], {
      is: (emp: boolean, cash: boolean) => emp === true || cash === true,
      then: (s) => s.trim().notRequired(),
      otherwise: (s) => s.notRequired(),
    }),
    cashTipStatus: yup
      .string()
      .oneOf(["", "unreported", "reported", "none"])
      .when("employmentIncome", {
        is: true,
        then: (s) =>
          s
            .oneOf(
              ["unreported", "reported", "none"],
              t(
                "affordable.application.householdIncome.cashTip.statusRequired"
              ) as string
            )
            .required(
              t(
                "affordable.application.householdIncome.cashTip.statusRequired"
              ) as string
            ),
        otherwise: (s) => s.notRequired(),
      }),
    cashTipFrequency: yup
      .string()
      .oneOf([
        "",
        "daily",
        "weekly",
        "bi-weekly",
        "semi-monthly",
        "monthly",
        "quarterly",
        "annually",
      ])
      .when("cashTipStatus", {
        is: "unreported",
        then: (s) =>
          s
            .oneOf(
              [
                "daily",
                "weekly",
                "bi-weekly",
                "semi-monthly",
                "monthly",
                "quarterly",
                "annually",
              ],
              t(
                "affordable.application.householdIncome.cashTip.frequencyRequired"
              ) as string
            )
            .required(
              t(
                "affordable.application.householdIncome.cashTip.frequencyRequired"
              ) as string
            ),
        otherwise: (s) => s.notRequired(),
      }),
    cashTipAmount: yup.string().when("cashTipStatus", {
      is: "unreported",
      then: (s) =>
        s
          .matches(
            /^\d+(\.\d{1,2})?$/,
            t(
              "affordable.application.householdIncome.cashTip.amountInvalid"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.cashTip.amountRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    cashTipNotTippedExplanation: yup.string().when("cashTipStatus", {
      is: "none",
      then: (s) =>
        s
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.notTippedExplanationRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    // Cash work earned-income amount + cadence — required only when the adult
    // said Yes to cashWork. Money-formatted like the other amount fields;
    // frequency reuses the week/month/annually value set.
    cashWorkAmount: yup.string().when("cashWork", {
      is: true,
      then: (s) =>
        s
          .matches(
            /^\d+(\.\d{1,2})?$/,
            t(
              "affordable.application.householdIncome.cashWorkAmountInvalid"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.cashWorkAmountRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    cashWorkFrequency: yup.string().when("cashWork", {
      is: true,
      then: (s) =>
        s
          .oneOf(["week", "bi-weekly", "month", "annually"])
          .required(
            t(
              "affordable.application.householdIncome.cashWorkFrequencyRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    additionalJobs: yup.array().of(
      yup.object({
        id: yup.string(),
        kind: yup
          .string()
          .oneOf(["wages", "cashWork", "selfEmployment"])
          .required(
            t(
              "affordable.application.householdIncome.additionalJobs.kindRequired"
            ) as string
          ),
        occupation: yup
          .string()
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.occupationRequired"
            ) as string
          ),
        employerName: yup
          .string()
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.employerNameRequired"
            ) as string
          ),
        employerAddress: yup
          .string()
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.employerAddressRequired"
            ) as string
          ),
        employerCityStateZip: yup
          .string()
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.employerCityStateZipRequired"
            ) as string
          ),
        employerWorkPhone: yup
          .string()
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.employerWorkPhoneRequired"
            ) as string
          ),
        employmentIndustry: yup
          .string()
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.industryRequired"
            ) as string
          ),
        employmentStartDate: yup
          .string()
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.startDateRequired"
            ) as string
          ),
        employmentEndDate: yup.string(),
        employmentCurrentlyEmployed: yup.boolean(),
        supervisorName: yup
          .string()
          .trim()
          .required(
            t(
              "affordable.application.householdIncome.cashTip.supervisorNameRequired"
            ) as string
          ),
        supervisorPhone: yup.string().trim().notRequired(),
        amount: yup
          .string()
          .matches(
            /^\d+(\.\d{1,2})?$/,
            t(
              "affordable.application.householdIncome.additionalJobs.amountInvalid"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.additionalJobs.amountRequired"
            ) as string
          ),
        frequency: yup.string().when("kind", {
          is: "cashWork",
          then: (s) =>
            s
              .oneOf(["week", "bi-weekly", "month", "annually"])
              .required(
                t(
                  "affordable.application.householdIncome.additionalJobs.frequencyRequired"
                ) as string
              ),
          otherwise: (s) =>
            s
              .oneOf([
                "weekly",
                "bi-weekly",
                "semi-monthly",
                "monthly",
                "annually",
              ])
              .required(
                t(
                  "affordable.application.householdIncome.additionalJobs.frequencyRequired"
                ) as string
              ),
        }),
      })
    ),
    // HCV gate: required when EITHER publicHousingSection8 OR zeroIncomeMember
    // is Yes. Inner fields (bedroom size, isPorting, expiry) are required only
    // once hasHcvVoucher === true.
    hasHcvVoucher: yup
      .boolean()
      .when(["publicHousingSection8", "zeroIncomeMember"], {
        is: (phs: boolean, zim: boolean) => phs === true || zim === true,
        then: (s) => s.typeError(requiredYesNoMsg).required(requiredYesNoMsg),
        otherwise: (s) => s.nullable().notRequired(),
      }),
    hcvBedroomSize: yup
      .string()
      .oneOf(["", "0", "1", "2", "3", "4", "5+"])
      .when("hasHcvVoucher", {
        is: true,
        then: (s) =>
          s
            .oneOf(
              ["0", "1", "2", "3", "4", "5+"],
              t(
                "affordable.application.householdIncome.hcv.bedroomSizeRequired"
              ) as string
            )
            .required(
              t(
                "affordable.application.householdIncome.hcv.bedroomSizeRequired"
              ) as string
            ),
        otherwise: (s) => s.notRequired(),
      }),
    hcvIsPorting: yup.boolean().when("hasHcvVoucher", {
      is: true,
      then: (s) => s.typeError(requiredYesNoMsg).required(requiredYesNoMsg),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    hcvIssuingPha: yup.string().default(""),
    hcvPortabilityExpires: yup.string().when("hcvIsPorting", {
      is: true,
      then: (s) =>
        s
          .required(
            t(
              "affordable.application.householdIncome.hcv.expiresRequired"
            ) as string
          )
          .matches(
            /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/,
            t(
              "affordable.application.householdIncome.hcv.expiresInvalid"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    anticipatedSpousalContribution: yup.boolean().when("maritalStatus", {
      is: (v: string) => v === "Separated" || v === "Estranged",
      then: (s) =>
        s
          .typeError(
            t(
              "affordable.application.householdIncome.spousalStatus.anticipatedSpousalContributionRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.spousalStatus.anticipatedSpousalContributionRequired"
            ) as string
          ),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    awardedIncome: yup.boolean().when("maritalStatus", {
      is: (v: string) => requiresAwardedIncome(v),
      then: (s) =>
        s
          .typeError(
            t(
              "affordable.application.householdIncome.spousalStatus.awardedIncomeRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.spousalStatus.awardedIncomeRequired"
            ) as string
          ),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    anticipatedAwardedIncome: yup.boolean().when("maritalStatus", {
      is: (v: string) => requiresAwardedIncome(v),
      then: (s) =>
        s
          .typeError(
            t(
              "affordable.application.householdIncome.spousalStatus.anticipatedAwardedIncomeRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.spousalStatus.anticipatedAwardedIncomeRequired"
            ) as string
          ),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    estrangedConfirmed: yup.boolean().when("maritalStatus", {
      is: "Estranged",
      then: (s) =>
        s
          .oneOf(
            [true],
            t(
              "affordable.application.householdIncome.spousalStatus.estrangedConfirmRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.spousalStatus.estrangedConfirmRequired"
            ) as string
          ),
      otherwise: (s) => s.nullable().notRequired(),
    }),
    estrangedInitials: yup.string().when("maritalStatus", {
      is: "Estranged",
      then: (s) =>
        s
          .min(
            1,
            t(
              "affordable.application.householdIncome.spousalStatus.estrangedInitialsRequired"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.spousalStatus.estrangedInitialsRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    // Child Support Cert validations gate on the user's selected status path.
    // Existence of the cert sub-step itself (i.e., "are there minors?") is
    // enforced in goNext via setError, since yup can't see the prop.
    childSupportChildIds: yup.array().of(yup.string().required()),
    childSupportStatus: yup.string().oneOf(["", "a", "b", "c", "d", "e"]),
    childSupportAmount: yup.string().when("childSupportStatus", {
      is: (st: string) => st === "a" || st === "b" || st === "c" || st === "d",
      then: (s) =>
        s
          .matches(
            /^\d+(\.\d{1,2})?$/,
            t(
              "affordable.application.householdIncome.childSupportAmountInvalid"
            ) as string
          )
          .required(
            t(
              "affordable.application.householdIncome.childSupportAmountRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    childSupportFrequency: yup.string().when("childSupportStatus", {
      is: (st: string) => st === "a" || st === "b",
      then: (s) =>
        s
          .oneOf(["week", "month", "annually"])
          .required(
            t(
              "affordable.application.householdIncome.childSupportFrequencyRequired"
            ) as string
          ),
      otherwise: (s) => s.notRequired(),
    }),
    // Recurring Gift Source — required when either Support & Assistance toggle
    // is Yes. Source receives the DocuSeal link directly per HUD chain-of-
    // custody rules; resident only provides name + email.
    recurringGiftSourceName: yup
      .string()
      .when(["cashAssistanceFromOthers", "billPaymentHelp"], {
        is: (cash: boolean, bill: boolean) => cash === true || bill === true,
        then: (s) =>
          s.required(
            t(
              "affordable.application.householdIncome.recurringGift.sourceNameRequired"
            ) as string
          ),
        otherwise: (s) => s.notRequired(),
      }),
    recurringGiftSourceEmail: yup
      .string()
      .when(["cashAssistanceFromOthers", "billPaymentHelp"], {
        is: (cash: boolean, bill: boolean) => cash === true || bill === true,
        then: (s) =>
          s
            .email(
              t(
                "affordable.application.householdIncome.recurringGift.sourceEmailInvalid"
              ) as string
            )
            .required(
              t(
                "affordable.application.householdIncome.recurringGift.sourceEmailRequired"
              ) as string
            ),
        otherwise: (s) => s.notRequired(),
      }),
    recurringGiftSourcePhone: yup.string().notRequired(),
    // Next-12-months pass — each anticipated item is required only when the
    // applicant has confirmed zero current income (zeroIncomeMember === true).
    // Otherwise the Next12 groups don't render and yup just nullable-allows
    // the values (which stay at UNSET).
    anticipatedIncome: yup.object().when("zeroIncomeMember", {
      is: true,
      then: () =>
        yup.object({
          employmentIncome: requiredYesNo(),
          selfEmployment: requiredYesNo(),
          cashWork: requiredYesNo(),
          unemployment: requiredYesNo(),
          vaGiBill: requiredYesNo(),
          militaryIncome: requiredYesNo(),
          publicAssistance: requiredYesNo(),
          ssRetirement: requiredYesNo(),
          ssiSsdi: requiredYesNo(),
          disabilityDeathBenefits: requiredYesNo(),
          workersComp: requiredYesNo(),
          cashAssistanceFromOthers: requiredYesNo(),
          billPaymentHelp: requiredYesNo(),
          trustsAnnuitiesPensions: requiredYesNo(),
          interestDividends: requiredYesNo(),
          realEstatePropertyIncome: requiredYesNo(),
          onlineIncome: requiredYesNo(),
          directSales: requiredYesNo(),
          educationAssistance: requiredYesNo(),
          unearnedIncomeMinors: requiredYesNo(),
          publicHousingSection8: requiredYesNo(),
          otherIncomeNotListed: requiredYesNo(),
          otherIncomeDescription: yup.string().when("otherIncomeNotListed", {
            is: true,
            then: (s) =>
              s.required(
                t(
                  "affordable.application.householdIncome.describeRequired"
                ) as string
              ),
            otherwise: (s) => s.notRequired(),
          }),
        }),
      otherwise: (s) => s.notRequired(),
    }),
    // Section C — all 14 expense pay-source fields are optional strings.
    // Blank values default to "N/A" in the cert prefill; applicants can
    // type a person's name, an entity, or "N/A" themselves. Yup just
    // accepts any string.
    expensesPaySources: yup.object({
      rent: yup.string(),
      utilities: yup.string(),
      food: yup.string(),
      clothing: yup.string(),
      schoolSupplies: yup.string(),
      phone: yup.string(),
      tvInternet: yup.string(),
      medical: yup.string(),
      medications: yup.string(),
      personalCare: yup.string(),
      vehicle: yup.string(),
      creditCards: yup.string(),
      other: yup.string(),
      additionalComments: yup.string(),
    }),
  });

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setError,
    setValue,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<HouseholdIncomeForm>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: defaultValues
      ? {
          ...defaultValues,
          additionalJobs: defaultValues.additionalJobs ?? [],
        }
      : DEFAULT_VALUES,
  });

  const {
    fields: additionalJobFields,
    append: appendAdditionalJob,
    remove: removeAdditionalJob,
  } = useFieldArray({
    control,
    name: "additionalJobs",
  });
  const additionalJobs = watch("additionalJobs") || [];

  const otherIncomeNotListed = watch("otherIncomeNotListed");
  const anticipatedOtherIncomeNotListed = watch(
    "anticipatedIncome.otherIncomeNotListed"
  );
  // We intentionally read this on every render rather than relying on a
  // memoized reference. RHF mutates the same object on nested-field
  // changes, so a memo dep on the object reference would never detect a
  // sub-field flip. hasAnyAnticipated below is derived as a primitive
  // boolean each render and is what we pass into effectiveGroups' deps.
  const anticipatedIncomeValues = watch("anticipatedIncome");
  const maritalStatus = watch("maritalStatus");
  const possessLegalDocs = watch("possessLegalDocs");
  const receivingSpousalContribution = watch("receivingSpousalContribution");
  const awardedIncome = watch("awardedIncome");
  const anticipatedSpousalContribution = watch(
    "anticipatedSpousalContribution"
  );
  const anticipatedAwardedIncome = watch("anticipatedAwardedIncome");
  const employmentIncome = watch("employmentIncome");
  const cashWork = watch("cashWork");
  const cashTipStatus = watch("cashTipStatus");
  const zeroIncomeMember = watch("zeroIncomeMember");
  const publicHousingSection8 = watch("publicHousingSection8");
  const hasHcvVoucher = watch("hasHcvVoucher");
  const hcvIsPorting = watch("hcvIsPorting");
  const childSupportStatus = watch("childSupportStatus");
  const childSupportChildIds = watch("childSupportChildIds");
  const cashAssistanceFromOthers = watch("cashAssistanceFromOthers");
  const billPaymentHelp = watch("billPaymentHelp");
  const needsRecurringGiftSource =
    cashAssistanceFromOthers === true || billPaymentHelp === true;
  const needsMaritalCertFields =
    maritalStatus === "Separated" || maritalStatus === "Estranged";
  // Awarded-income (alimony/child support/survivor) questions also show for
  // Divorced (TAA Supplemental), without the separation-cert fields above.
  const needsAwardedIncomeFields = requiresAwardedIncome(maritalStatus);

  // hasAnyIncome: true if the applicant said Yes to ANY income item other
  // than zeroIncomeMember itself. Also includes signals from the
  // spousal-status and child-support cert sub-steps (which aren't in the
  // 22-item boolean list but represent real income):
  //   - awardedIncome: legally awarded alimony / child support / survivor
  //   - receivingSpousalContribution: actively receiving spousal money
  //   - childSupportStatus a/c/d: currently receiving or anticipating
  //     child support in the next 12 months (b and e are explicit "none")
  // When true, the zeroIncome screen is skipped and zeroIncomeMember is
  // forced to false (see useEffect below).
  const watchedIncomeValues = watch(INCOME_ITEMS_NON_ZERO);
  const hasAnyIncome =
    watchedIncomeValues.some((v) => v === true) ||
    awardedIncome === true ||
    receivingSpousalContribution === true ||
    childSupportStatus === "a" ||
    childSupportStatus === "c" ||
    childSupportStatus === "d";

  // Keep zeroIncomeMember in sync with hasAnyIncome:
  //   - When the applicant has any income, force zeroIncomeMember=false so
  //     the form is internally consistent AND yup.boolean().required()
  //     passes even though the user never sees the zeroIncome screen.
  //   - When hasAnyIncome transitions back from true → false (the applicant
  //     went back and un-selected their income), reset zeroIncomeMember to
  //     UNSET so the toggle on the zeroIncome screen starts unselected
  //     instead of carrying over the previously-forced false.
  const prevHasAnyIncomeRef = useRef(hasAnyIncome);
  useEffect(() => {
    if (hasAnyIncome) {
      setValue("zeroIncomeMember", false);
    } else if (prevHasAnyIncomeRef.current === true) {
      setValue("zeroIncomeMember", UNSET);
    }
    prevHasAnyIncomeRef.current = hasAnyIncome;
  }, [hasAnyIncome, setValue]);

  // When zeroIncomeMember flips away from true (either because the applicant
  // clicked No on the confirmation screen, or because hasAnyIncome forced it
  // false via the effect above), reset the next-12-months pass back to UNSET
  // so stale answers can't sneak through to the cert if the applicant later
  // re-confirms zero income.
  useEffect(() => {
    if (zeroIncomeMember !== true) {
      setValue("anticipatedIncome", DEFAULT_VALUES.anticipatedIncome, {
        shouldValidate: false,
      });
    }
  }, [zeroIncomeMember, setValue]);

  // Internal sub-step — shows one group at a time within this step.
  // Does not affect the outer stepper; all groups share one form state.
  // Filters:
  //   - childSupportCert: skipped when the household has no minors.
  //   - zeroIncome: skipped when the applicant has any income (every cert
  //     consumer reads zeroIncomeMember directly, which the useEffect above
  //     keeps in sync with hasAnyIncome).
  //   - *Next12: only shown when zeroIncomeMember === true (the applicant
  //     just confirmed zero current income on the zeroIncome screen).
  // Computed in the body (not in useMemo) so it re-evaluates every render
  // and stays in sync with RHF's mutated `anticipatedIncome` object. This
  // primitive boolean is what we pass into effectiveGroups' deps.
  const hasAnyAnticipated =
    (anticipatedIncomeValues && typeof anticipatedIncomeValues === "object"
      ? Object.entries(anticipatedIncomeValues).some(
          ([key, v]) => key !== "otherIncomeDescription" && v === true
        )
      : false) ||
    childSupportStatus === "d" ||
    anticipatedSpousalContribution === true ||
    anticipatedAwardedIncome === true;

  const effectiveGroups = useMemo(() => {
    let g = GROUPS;
    if (householdMinors.length === 0) {
      g = g.filter((x) => x.groupKey !== "childSupportCert");
    }
    // Employment-details sub-step (occupation, employer, cash-tip cert, and the
    // cash-work amount/frequency) renders when the adult declared employment
    // income OR cash work — both are earned income needing an employer of
    // record. Adults with neither skip the screen entirely.
    if (employmentIncome !== true && cashWork !== true) {
      g = g.filter((x) => x.groupKey !== "cashTip");
    }
    if (hasAnyIncome) {
      g = g.filter((x) => x.groupKey !== "zeroIncome");
    }
    if (zeroIncomeMember !== true) {
      g = g.filter((x) => !isNext12(x.groupKey));
    }
    // expensesPaySources (Section C) renders only when the applicant has
    // both zero current income AND no anticipated income — exactly the
    // path where the cert form's Section C asks who pays for expenses.
    if (zeroIncomeMember !== true || hasAnyAnticipated) {
      g = g.filter((x) => x.groupKey !== "expensesPaySources");
    }
    // HCV sub-step renders only when the applicant said Yes to
    // publicHousingSection8 OR declared zero income.
    if (publicHousingSection8 !== true && zeroIncomeMember !== true) {
      g = g.filter((x) => x.groupKey !== "hcv");
    }
    return g;
  }, [
    householdMinors.length,
    employmentIncome,
    cashWork,
    hasAnyIncome,
    zeroIncomeMember,
    hasAnyAnticipated,
    publicHousingSection8,
  ]);
  // Dev/QA helper: ?subStep=<groupKey> in the URL jumps straight into a
  // specific sub-step on first render. Useful for re-testing a specific
  // sub-step without clicking Next through the earlier ones. Falls back to 0
  // (spousalStatus) if the param is missing or doesn't match a group.
  const initialSubStep = useMemo(() => {
    if (typeof window === "undefined") return 0;
    const params = new URLSearchParams(window.location.search);
    const target = params.get("subStep");
    if (!target) return 0;
    const idx = effectiveGroups.findIndex((g) => g.groupKey === target);
    return idx >= 0 ? idx : 0;
  }, [effectiveGroups]);
  const [subStep, setSubStep] = useState(initialSubStep);
  const isFirstSubStep = subStep === 0;
  const isLastSubStep = subStep === effectiveGroups.length - 1;
  const currentGroup = effectiveGroups[subStep];
  // The "cashTip" sub-step is shared: employed adults see it for the tip-status
  // declaration, while cash-only workers land here just for the employer +
  // cash-work amount fields. Swap to a cash-work-specific heading in the latter
  // case so a non-employed cash worker isn't shown a "Cash Tip Income" title.
  const currentGroupI18nKey =
    currentGroup?.groupKey === "cashTip" && employmentIncome !== true
      ? "cashWork"
      : currentGroup?.groupKey;

  const goBack = () => {
    if (isFirstSubStep) {
      onBack();
    } else {
      setSubStep(subStep - 1);
    }
  };

  const goNext = async () => {
    // Income groups: every Yes/No item on the screen must be explicitly
    // answered before advancing. Items default to UNSET so the toggle starts
    // unselected; trigger() runs the yup.boolean().required() check.
    const INCOME_GROUP_KEYS = [
      "employment",
      "benefits",
      "socialSecurity",
      "support",
      "other",
      "zeroIncome",
      "anyOther",
    ];
    if (INCOME_GROUP_KEYS.includes(currentGroup.groupKey)) {
      const ok = await trigger(
        currentGroup.items as (keyof HouseholdIncomeForm)[]
      );
      if (!ok) return;
      // Employment sub-step: when current employmentIncome is No, also
      // require the inline pendingJobOffer Yes/No (non-employed cert
      // Section B signal).
      if (
        currentGroup.groupKey === "employment" &&
        employmentIncome === false
      ) {
        const pjoOk = await trigger("pendingJobOffer");
        if (!pjoOk) return;
      }
    }
    // Next-12-months pass — same Yes/No requirement, but the Controller
    // names are nested under anticipatedIncome.<item>, so trigger needs
    // the dot-pathed field names.
    if (isNext12(currentGroup.groupKey)) {
      const fields = currentGroup.items.map(
        (k) => `anticipatedIncome.${k}` as `anticipatedIncome.${IncomeItemKey}`
      );
      const ok = await trigger(fields as any);
      if (!ok) return;
      // anyOtherNext12: when "any other anticipated income?" is Yes, also
      // require the free-text description so cert Section B can surface it.
      if (
        currentGroup.groupKey === "anyOtherNext12" &&
        anticipatedOtherIncomeNotListed === true
      ) {
        const descOk = await trigger(
          "anticipatedIncome.otherIncomeDescription" as any
        );
        if (!descOk) return;
      }
    }
    // spousalStatus sub-step has its own required validations driven by yup
    // schema. Advance only when the relevant conditional fields are valid.
    if (currentGroup.groupKey === "spousalStatus") {
      const fields: (keyof HouseholdIncomeForm)[] = ["maritalStatus"];
      if (needsAwardedIncomeFields) {
        fields.push("awardedIncome", "anticipatedAwardedIncome");
      }
      if (needsMaritalCertFields) {
        fields.push(
          "spouseName",
          "receivingSpousalContribution",
          "anticipatedSpousalContribution",
          "possessLegalDocs"
        );
        if (receivingSpousalContribution === true) {
          fields.push("spousalContributionAmount");
        }
        if (possessLegalDocs === false) {
          fields.push("noLegalDocsReason");
        }
        if (maritalStatus === "Estranged") {
          fields.push("estrangedConfirmed", "estrangedInitials");
        }
      }
      const ok = await trigger(fields);
      if (!ok) return;
    }
    if (currentGroup.groupKey === "childSupportCert") {
      // yup gates amount/freq validity on status; children + status presence
      // are enforced manually since yup can't see the householdMinors prop.
      let manualOk = true;
      if (!childSupportChildIds || childSupportChildIds.length === 0) {
        setError("childSupportChildIds", {
          type: "required",
          message: t(
            "affordable.application.householdIncome.childSupportChildIdsRequired"
          ) as string,
        });
        manualOk = false;
      } else {
        clearErrors("childSupportChildIds");
      }
      if (!childSupportStatus) {
        setError("childSupportStatus", {
          type: "required",
          message: t(
            "affordable.application.householdIncome.childSupportStatusRequired"
          ) as string,
        });
        manualOk = false;
      } else {
        clearErrors("childSupportStatus");
      }
      const fields: (keyof HouseholdIncomeForm)[] = [];
      if (
        childSupportStatus === "a" ||
        childSupportStatus === "b" ||
        childSupportStatus === "c" ||
        childSupportStatus === "d"
      ) {
        fields.push("childSupportAmount");
      }
      if (childSupportStatus === "a" || childSupportStatus === "b") {
        fields.push("childSupportFrequency");
      }
      const yupOk = fields.length ? await trigger(fields) : true;
      if (!manualOk || !yupOk) return;
    }
    // Support & Assistance: when either toggle is Yes, require gift-source
    // name + email so the per-adult bundle can include the source submitter.
    if (currentGroup.groupKey === "support" && needsRecurringGiftSource) {
      const ok = await trigger([
        "recurringGiftSourceName",
        "recurringGiftSourceEmail",
      ]);
      if (!ok) return;
    }
    // Cash Tip sub-step — occupation + status are always required when this
    // group renders (gated on employmentIncome === true). frequency+amount
    // gated on 'unreported'; explanation gated on 'none'. yup carries the
    // conditional gates; we just list every potentially-relevant field.
    if (currentGroup.groupKey === "cashTip") {
      const fields: (keyof HouseholdIncomeForm)[] = [];
      if (employmentIncome === true) {
        fields.push("employmentIncomeFrequency", "employmentIncomeAmount");
      }
      fields.push(
        "occupation",
        "employerName",
        "employerAddress",
        "employerCityStateZip",
        "employerWorkPhone",
        "employmentIndustry",
        "employmentStartDate",
        "supervisorName",
        "supervisorPhone",
        "cashTipStatus"
      );
      if (cashTipStatus === "unreported") {
        fields.push("cashTipFrequency", "cashTipAmount");
      } else if (cashTipStatus === "none") {
        fields.push("cashTipNotTippedExplanation");
      }
      // Cash work amount + frequency are required whenever cash work is Yes,
      // independent of the tip-status path (which is employment-only).
      if (cashWork === true) {
        fields.push("cashWorkFrequency", "cashWorkAmount");
      }
      const ok = await trigger([...fields, "additionalJobs"]);
      if (!ok) return;
    }
    // Zero-income confirmation: clicking No here contradicts the prior
    // pass (every current item was answered No to reach this screen). The
    // applicant must either confirm Yes or go back and update an income
    // item to advance.
    if (currentGroup.groupKey === "zeroIncome" && zeroIncomeMember === false) {
      return;
    }
    // HCV sub-step: hasHcvVoucher is always required when this screen renders.
    // Inner fields (bedroom size, isPorting, expiry, PHA) are required only
    // when hasHcvVoucher === true / hcvIsPorting === true respectively.
    if (currentGroup.groupKey === "hcv") {
      const fields: (keyof HouseholdIncomeForm)[] = ["hasHcvVoucher"];
      if (hasHcvVoucher === true) {
        fields.push("hcvBedroomSize", "hcvIsPorting");
        if (hcvIsPorting === true) fields.push("hcvPortabilityExpires");
      }
      const ok = await trigger(fields);
      if (!ok) return;
    }
    if (isLastSubStep) {
      handleSubmit(onSubmit)();
    } else {
      setSubStep(subStep + 1);
    }
  };

  const renderRow = (
    itemKey: IncomeItemKey,
    Icon: IncomeGroup["Icon"],
    // Defaults to itemKey (top-level current-state field). For Next12 groups
    // the caller passes `anticipatedIncome.${itemKey}` so the same row UI
    // binds to the nested anticipated value.
    fieldPath?: string
  ) => (
    <Controller
      key={fieldPath ?? itemKey}
      name={(fieldPath ?? itemKey) as any}
      control={control}
      render={({ field, fieldState }) => {
        const isYes = field.value === true;
        const hasError = !!fieldState.error;
        return (
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                border: `1px solid ${
                  hasError
                    ? COLORS.red[500]
                    : isYes
                    ? COLORS.darkBlue[500]
                    : COLORS.gray[200]
                }`,
                borderRadius: 2,
                backgroundColor: isYes ? COLORS.blue[50] : "transparent",
                transition: "all 0.2s ease",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: COLORS.gray[100],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ color: COLORS.gray[600], fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {t(
                    `affordable.application.householdIncome.items.${itemKey}.title`
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.25 }}
                >
                  {t(
                    `affordable.application.householdIncome.items.${itemKey}.description`
                  )}
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={
                  field.value === true
                    ? "yes"
                    : field.value === false
                    ? "no"
                    : null
                }
                onChange={(_, val) => {
                  if (val !== null) field.onChange(val === "yes");
                }}
                sx={{ flexShrink: 0 }}
              >
                <ToggleButton value="yes" sx={{ px: 2 }}>
                  {t("affordable.application.householdIncome.yes")}
                </ToggleButton>
                <ToggleButton value="no" sx={{ px: 2 }}>
                  {t("affordable.application.householdIncome.no")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {isYes &&
              !fieldPath &&
              (BENEFIT_AMOUNT_KEYS as readonly string[]).includes(itemKey) && (
                <Controller
                  name={`${itemKey}Amount` as any}
                  control={control}
                  render={({ field: amtField, fieldState: amtState }) => (
                    <Box sx={{ mt: 1.5, ml: 7 }}>
                      <TextField
                        {...amtField}
                        fullWidth
                        required
                        label={t(
                          "affordable.application.householdIncome.benefitAmountLabel"
                        )}
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: <Box sx={{ mr: 0.5 }}>$</Box>,
                          endAdornment: (
                            <Box sx={{ ml: 0.5, color: "text.secondary" }}>
                              {t(
                                "affordable.application.householdIncome.benefitAmountPerMonth"
                              )}
                            </Box>
                          ),
                        }}
                        error={!!amtState.error}
                        helperText={
                          amtState.error?.message ??
                          (t(
                            "affordable.application.householdIncome.benefitAmountHelper"
                          ) as string)
                        }
                      />
                    </Box>
                  )}
                />
              )}
            {hasError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5, ml: 7 }}
              >
                {fieldState.error?.message}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );

  // The next-12-months pass introduces itself with a one-line banner above
  // the first *Next12 group's title, so the applicant understands why the
  // income list is reappearing after they just confirmed zero income.
  const showNext12Intro = currentGroup.groupKey === "employmentNext12";

  return (
    <Box component="form" onSubmit={(e) => e.preventDefault()}>
      <RegisterReviewSection>
        {showNext12Intro && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: COLORS.blue[50],
              border: `1px solid ${COLORS.darkBlue[500]}33`,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("affordable.application.householdIncome.groups.next12Intro")}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            mb: 0.5,
          }}
        >
          <RegisterReviewSectionTitle sx={{ mb: 0 }}>
            {t(
              `affordable.application.householdIncome.groups.${currentGroupI18nKey}.title`
            )}
          </RegisterReviewSectionTitle>
          <Typography variant="body2" color="text.secondary">
            {subStep + 1} / {effectiveGroups.length}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            `affordable.application.householdIncome.groups.${currentGroupI18nKey}.subtitle`
          )}
        </Typography>
        {currentGroup.items.map((itemKey) =>
          renderRow(
            itemKey,
            currentGroup.Icon,
            isNext12(currentGroup.groupKey)
              ? `anticipatedIncome.${itemKey}`
              : undefined
          )
        )}

        {currentGroup.groupKey === "employment" &&
          employmentIncome === false && (
            <Box sx={{ mt: 1.5 }}>
              <Controller
                name="pendingJobOffer"
                control={control}
                render={({ field }) => (
                  <FormControl error={!!errors.pendingJobOffer}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {t(
                        "affordable.application.householdIncome.items.pendingJobOffer.title"
                      )}{" "}
                      *
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5, fontWeight: 400 }}
                    >
                      {t(
                        "affordable.application.householdIncome.items.pendingJobOffer.description"
                      )}
                    </Typography>
                    <RadioGroup
                      row
                      value={
                        field.value === true
                          ? "yes"
                          : field.value === false
                          ? "no"
                          : ""
                      }
                      onChange={(_, val) =>
                        field.onChange(val === "yes" ? true : false)
                      }
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label={t("affordable.application.householdIncome.yes")}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label={t("affordable.application.householdIncome.no")}
                      />
                    </RadioGroup>
                    {errors.pendingJobOffer && (
                      <FormHelperText>
                        {errors.pendingJobOffer.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>
          )}

        {currentGroup.groupKey === "cashTip" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Controller
              name="occupation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t(
                    "affordable.application.householdIncome.cashTip.occupationLabel"
                  )}
                  placeholder={
                    t(
                      "affordable.application.householdIncome.cashTip.occupationPlaceholder"
                    ) as string
                  }
                  error={!!errors.occupation}
                  helperText={errors.occupation?.message}
                />
              )}
            />
            <Controller
              name="employmentIndustry"
              control={control}
              render={({ field }) => (
                <FormControl
                  error={!!errors.employmentIndustry}
                  fullWidth
                  required
                >
                  <InputLabel id="employmentIndustry-label">
                    {t(
                      "affordable.application.householdIncome.cashTip.industryLabel"
                    )}
                  </InputLabel>
                  <Select
                    {...field}
                    value={field.value || ""}
                    labelId="employmentIndustry-label"
                    label={t(
                      "affordable.application.householdIncome.cashTip.industryLabel"
                    )}
                  >
                    {RESMAN_INDUSTRIES.map((industry) => (
                      <MenuItem key={industry} value={industry}>
                        {industry}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.employmentIndustry && (
                    <FormHelperText>
                      {errors.employmentIndustry.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            {employmentIncome === true && (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <Controller
                  name="employmentIncomeFrequency"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      error={!!errors.employmentIncomeFrequency}
                      fullWidth
                      required
                    >
                      <InputLabel id="employmentIncomeFrequency-label">
                        {t(
                          "affordable.application.householdIncome.employmentIncomeFrequencyLabel"
                        )}
                      </InputLabel>
                      <Select
                        {...field}
                        value={field.value || ""}
                        labelId="employmentIncomeFrequency-label"
                        label={t(
                          "affordable.application.householdIncome.employmentIncomeFrequencyLabel"
                        )}
                      >
                        <MenuItem value="weekly">
                          {t(
                            "affordable.application.householdIncome.employmentIncomeFrequencyOptions.weekly"
                          )}
                        </MenuItem>
                        <MenuItem value="bi-weekly">
                          {t(
                            "affordable.application.householdIncome.employmentIncomeFrequencyOptions.biWeekly"
                          )}
                        </MenuItem>
                        <MenuItem value="semi-monthly">
                          {t(
                            "affordable.application.householdIncome.employmentIncomeFrequencyOptions.semiMonthly"
                          )}
                        </MenuItem>
                        <MenuItem value="monthly">
                          {t(
                            "affordable.application.householdIncome.employmentIncomeFrequencyOptions.monthly"
                          )}
                        </MenuItem>
                        <MenuItem value="annually">
                          {t(
                            "affordable.application.householdIncome.employmentIncomeFrequencyOptions.annually"
                          )}
                        </MenuItem>
                      </Select>
                      {errors.employmentIncomeFrequency && (
                        <FormHelperText>
                          {errors.employmentIncomeFrequency.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name="employmentIncomeAmount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label={t(
                        "affordable.application.householdIncome.employmentIncomeAmountLabel"
                      )}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: <Box sx={{ mr: 0.5 }}>$</Box>,
                      }}
                      error={!!errors.employmentIncomeAmount}
                      helperText={
                        errors.employmentIncomeAmount?.message ??
                        (t(
                          "affordable.application.householdIncome.employmentIncomeAmountHelper"
                        ) as string)
                      }
                    />
                  )}
                />
              </Box>
            )}
            <Controller
              name="employerName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t(
                    "affordable.application.householdIncome.cashTip.employerNameLabel"
                  )}
                  error={!!errors.employerName}
                  helperText={errors.employerName?.message}
                />
              )}
            />
            <Controller
              name="employerAddress"
              control={control}
              render={({ field }) => (
                <GoogleSearchAutoComplete
                  id="employer-address"
                  label={`${t(
                    "affordable.application.householdIncome.cashTip.employerAddressLabel"
                  )} *`}
                  value={field.value || ""}
                  error={errors.employerAddress?.message}
                  isValid={!errors.employerAddress}
                  onInputChange={(val: string) => field.onChange(val ?? "")}
                  onSelect={(addr: {
                    line1?: string;
                    city?: string;
                    state?: string;
                    zip?: string;
                  }) => {
                    field.onChange(addr.line1 || "");
                    setValue("employerCityStateZip", formatCityStateZip(addr), {
                      shouldValidate: true,
                    });
                  }}
                />
              )}
            />
            <Controller
              name="employerCityStateZip"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t(
                    "affordable.application.householdIncome.cashTip.employerCityStateZipLabel"
                  )}
                  placeholder={
                    t(
                      "affordable.application.householdIncome.cashTip.employerCityStateZipPlaceholder"
                    ) as string
                  }
                  error={!!errors.employerCityStateZip}
                  helperText={errors.employerCityStateZip?.message}
                />
              )}
            />
            <Controller
              name="employerWorkPhone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t(
                    "affordable.application.householdIncome.cashTip.employerWorkPhoneLabel"
                  )}
                  placeholder="(555) 555-5555"
                  error={!!errors.employerWorkPhone}
                  helperText={errors.employerWorkPhone?.message}
                  onChange={(e) => field.onChange(formatPhone(e.target.value))}
                />
              )}
            />
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              }}
            >
              <Controller
                name="employmentStartDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="date"
                    required
                    InputLabelProps={{ shrink: true }}
                    label={t(
                      "affordable.application.householdIncome.cashTip.startDateLabel"
                    )}
                    error={!!errors.employmentStartDate}
                    helperText={errors.employmentStartDate?.message}
                  />
                )}
              />
              <Box>
                <Controller
                  name="employmentCurrentlyEmployed"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            if (e.target.checked) {
                              setValue("employmentEndDate", "", {
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                      }
                      label={t(
                        "affordable.application.householdIncome.cashTip.currentlyEmployedLabel",
                        "I currently work here"
                      )}
                    />
                  )}
                />
                {!watch("employmentCurrentlyEmployed") && (
                  <Controller
                    name="employmentEndDate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        label={t(
                          "affordable.application.householdIncome.cashTip.endDateLabel"
                        )}
                        sx={{ mt: 1 }}
                      />
                    )}
                  />
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              }}
            >
              <Controller
                name="supervisorName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label={t(
                      "affordable.application.householdIncome.cashTip.supervisorNameLabel"
                    )}
                    error={!!errors.supervisorName}
                    helperText={errors.supervisorName?.message}
                  />
                )}
              />
              <Controller
                name="supervisorPhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t(
                      "affordable.application.householdIncome.cashTip.supervisorPhoneLabel"
                    )}
                    placeholder="(555) 555-5555"
                    error={!!errors.supervisorPhone}
                    helperText={
                      errors.supervisorPhone?.message ??
                      (t(
                        "affordable.application.householdIncome.cashTip.supervisorPhoneOptional",
                        "Optional"
                      ) as string)
                    }
                    onChange={(e) =>
                      field.onChange(formatPhone(e.target.value))
                    }
                  />
                )}
              />
            </Box>
            {cashWork === true && (
              <>
                <Controller
                  name="cashWorkFrequency"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      error={!!errors.cashWorkFrequency}
                      fullWidth
                      required
                    >
                      <InputLabel id="cashWorkFrequency-label">
                        {t(
                          "affordable.application.householdIncome.cashWorkFrequencyLabel"
                        )}
                      </InputLabel>
                      <Select
                        {...field}
                        value={field.value || ""}
                        labelId="cashWorkFrequency-label"
                        label={t(
                          "affordable.application.householdIncome.cashWorkFrequencyLabel"
                        )}
                      >
                        <MenuItem value="week">
                          {t(
                            "affordable.application.householdIncome.childSupportFrequencyOptions.week"
                          )}
                        </MenuItem>
                        <MenuItem value="bi-weekly">
                          {t(
                            "affordable.application.householdIncome.employmentIncomeFrequencyOptions.biWeekly"
                          )}
                        </MenuItem>
                        <MenuItem value="month">
                          {t(
                            "affordable.application.householdIncome.childSupportFrequencyOptions.month"
                          )}
                        </MenuItem>
                        <MenuItem value="annually">
                          {t(
                            "affordable.application.householdIncome.childSupportFrequencyOptions.annually"
                          )}
                        </MenuItem>
                      </Select>
                      {errors.cashWorkFrequency && (
                        <FormHelperText>
                          {errors.cashWorkFrequency.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name="cashWorkAmount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label={t(
                        "affordable.application.householdIncome.cashWorkAmountLabel"
                      )}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: <Box sx={{ mr: 0.5 }}>$</Box>,
                      }}
                      error={!!errors.cashWorkAmount}
                      helperText={
                        errors.cashWorkAmount?.message ??
                        (t(
                          "affordable.application.householdIncome.cashWorkAmountHelper"
                        ) as string)
                      }
                    />
                  )}
                />
              </>
            )}
            {employmentIncome === true && (
              <>
                <Controller
                  name="cashTipStatus"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.cashTipStatus} fullWidth>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t(
                          "affordable.application.householdIncome.cashTip.statusLabel"
                        )}{" "}
                        *
                      </Typography>
                      <RadioGroup
                        value={field.value || ""}
                        onChange={(_, val) => field.onChange(val)}
                      >
                        <FormControlLabel
                          value="unreported"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.cashTip.statusUnreported"
                          )}
                        />
                        <FormControlLabel
                          value="reported"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.cashTip.statusReported"
                          )}
                        />
                        <FormControlLabel
                          value="none"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.cashTip.statusNone"
                          )}
                        />
                      </RadioGroup>
                      {errors.cashTipStatus && (
                        <FormHelperText>
                          {errors.cashTipStatus.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                {cashTipStatus === "unreported" && (
                  <>
                    <Controller
                      name="cashTipFrequency"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          error={!!errors.cashTipFrequency}
                          fullWidth
                          required
                        >
                          <InputLabel id="cashTipFrequency-label">
                            {t(
                              "affordable.application.householdIncome.cashTip.frequencyLabel"
                            )}
                          </InputLabel>
                          <Select
                            {...field}
                            value={field.value || ""}
                            labelId="cashTipFrequency-label"
                            label={t(
                              "affordable.application.householdIncome.cashTip.frequencyLabel"
                            )}
                          >
                            <MenuItem value="daily">
                              {t(
                                "affordable.application.householdIncome.cashTip.frequencyDaily"
                              )}
                            </MenuItem>
                            <MenuItem value="weekly">
                              {t(
                                "affordable.application.householdIncome.cashTip.frequencyWeekly"
                              )}
                            </MenuItem>
                            <MenuItem value="bi-weekly">
                              {t(
                                "affordable.application.householdIncome.cashTip.frequencyBiWeekly"
                              )}
                            </MenuItem>
                            <MenuItem value="semi-monthly">
                              {t(
                                "affordable.application.householdIncome.cashTip.frequencySemiMonthly"
                              )}
                            </MenuItem>
                            <MenuItem value="monthly">
                              {t(
                                "affordable.application.householdIncome.cashTip.frequencyMonthly"
                              )}
                            </MenuItem>
                            <MenuItem value="quarterly">
                              {t(
                                "affordable.application.householdIncome.cashTip.frequencyQuarterly"
                              )}
                            </MenuItem>
                            <MenuItem value="annually">
                              {t(
                                "affordable.application.householdIncome.cashTip.frequencyAnnually"
                              )}
                            </MenuItem>
                          </Select>
                          {errors.cashTipFrequency && (
                            <FormHelperText>
                              {errors.cashTipFrequency.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="cashTipAmount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label={t(
                            "affordable.application.householdIncome.cashTip.amountLabel"
                          )}
                          placeholder="0.00"
                          InputProps={{
                            startAdornment: <Box sx={{ mr: 0.5 }}>$</Box>,
                          }}
                          error={!!errors.cashTipAmount}
                          helperText={
                            errors.cashTipAmount?.message ??
                            (t(
                              "affordable.application.householdIncome.cashTip.amountHelper"
                            ) as string)
                          }
                        />
                      )}
                    />
                  </>
                )}
                {cashTipStatus === "none" && (
                  <Controller
                    name="cashTipNotTippedExplanation"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        multiline
                        minRows={2}
                        label={t(
                          "affordable.application.householdIncome.cashTip.notTippedExplanationLabel"
                        )}
                        placeholder={
                          t(
                            "affordable.application.householdIncome.cashTip.notTippedExplanationPlaceholder"
                          ) as string
                        }
                        error={!!errors.cashTipNotTippedExplanation}
                        helperText={errors.cashTipNotTippedExplanation?.message}
                      />
                    )}
                  />
                )}
              </>
            )}

            <Box
              sx={{
                mt: 1,
                pt: 2,
                borderTop: `1px solid ${COLORS.gray[200]}`,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(
                    "affordable.application.householdIncome.additionalJobs.title"
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {t(
                    "affordable.application.householdIncome.additionalJobs.helper"
                  )}
                </Typography>
              </Box>

              {additionalJobFields.map((jobField, index) => {
                const jobKind = additionalJobs[index]?.kind || "wages";
                const jobErrors = errors.additionalJobs?.[index] as
                  | Partial<Record<keyof AdditionalJob, { message?: string }>>
                  | undefined;
                const isCashJob = jobKind === "cashWork";
                return (
                  <Box
                    key={jobField.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${COLORS.darkBlue[500]}33`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(
                          "affordable.application.householdIncome.additionalJobs.jobLabel",
                          { n: index + 2 }
                        )}
                      </Typography>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => removeAdditionalJob(index)}
                      >
                        {t(
                          "affordable.application.householdIncome.additionalJobs.remove"
                        )}
                      </Button>
                    </Box>
                    <Controller
                      name={`additionalJobs.${index}.kind`}
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          error={!!jobErrors?.kind}
                          fullWidth
                          required
                        >
                          <InputLabel id={`additionalJob-${index}-kind-label`}>
                            {t(
                              "affordable.application.householdIncome.additionalJobs.kindLabel"
                            )}
                          </InputLabel>
                          <Select
                            {...field}
                            value={field.value || "wages"}
                            labelId={`additionalJob-${index}-kind-label`}
                            label={t(
                              "affordable.application.householdIncome.additionalJobs.kindLabel"
                            )}
                            onChange={(e) => {
                              field.onChange(e);
                              setValue(
                                `additionalJobs.${index}.frequency`,
                                "",
                                { shouldValidate: false }
                              );
                            }}
                          >
                            <MenuItem value="wages">
                              {t(
                                "affordable.application.householdIncome.additionalJobs.kindOptions.wages"
                              )}
                            </MenuItem>
                            <MenuItem value="cashWork">
                              {t(
                                "affordable.application.householdIncome.additionalJobs.kindOptions.cashWork"
                              )}
                            </MenuItem>
                            <MenuItem value="selfEmployment">
                              {t(
                                "affordable.application.householdIncome.additionalJobs.kindOptions.selfEmployment"
                              )}
                            </MenuItem>
                          </Select>
                          {jobErrors?.kind && (
                            <FormHelperText>
                              {jobErrors.kind.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                    <Controller
                      name={`additionalJobs.${index}.occupation`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label={t(
                            "affordable.application.householdIncome.cashTip.occupationLabel"
                          )}
                          error={!!jobErrors?.occupation}
                          helperText={jobErrors?.occupation?.message}
                        />
                      )}
                    />
                    <Controller
                      name={`additionalJobs.${index}.employmentIndustry`}
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          error={!!jobErrors?.employmentIndustry}
                          fullWidth
                          required
                        >
                          <InputLabel
                            id={`additionalJob-${index}-industry-label`}
                          >
                            {t(
                              "affordable.application.householdIncome.cashTip.industryLabel"
                            )}
                          </InputLabel>
                          <Select
                            {...field}
                            value={field.value || ""}
                            labelId={`additionalJob-${index}-industry-label`}
                            label={t(
                              "affordable.application.householdIncome.cashTip.industryLabel"
                            )}
                          >
                            {RESMAN_INDUSTRIES.map((industry) => (
                              <MenuItem key={industry} value={industry}>
                                {industry}
                              </MenuItem>
                            ))}
                          </Select>
                          {jobErrors?.employmentIndustry && (
                            <FormHelperText>
                              {jobErrors.employmentIndustry.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      }}
                    >
                      <Controller
                        name={`additionalJobs.${index}.frequency`}
                        control={control}
                        render={({ field }) => (
                          <FormControl
                            error={!!jobErrors?.frequency}
                            fullWidth
                            required
                          >
                            <InputLabel
                              id={`additionalJob-${index}-frequency-label`}
                            >
                              {isCashJob
                                ? t(
                                    "affordable.application.householdIncome.cashWorkFrequencyLabel"
                                  )
                                : t(
                                    "affordable.application.householdIncome.employmentIncomeFrequencyLabel"
                                  )}
                            </InputLabel>
                            <Select
                              {...field}
                              value={field.value || ""}
                              labelId={`additionalJob-${index}-frequency-label`}
                              label={
                                isCashJob
                                  ? t(
                                      "affordable.application.householdIncome.cashWorkFrequencyLabel"
                                    )
                                  : t(
                                      "affordable.application.householdIncome.employmentIncomeFrequencyLabel"
                                    )
                              }
                            >
                              {(isCashJob
                                ? [
                                    [
                                      "week",
                                      "childSupportFrequencyOptions.week",
                                    ],
                                    [
                                      "bi-weekly",
                                      "employmentIncomeFrequencyOptions.biWeekly",
                                    ],
                                    [
                                      "month",
                                      "childSupportFrequencyOptions.month",
                                    ],
                                    [
                                      "annually",
                                      "childSupportFrequencyOptions.annually",
                                    ],
                                  ]
                                : [
                                    [
                                      "weekly",
                                      "employmentIncomeFrequencyOptions.weekly",
                                    ],
                                    [
                                      "bi-weekly",
                                      "employmentIncomeFrequencyOptions.biWeekly",
                                    ],
                                    [
                                      "semi-monthly",
                                      "employmentIncomeFrequencyOptions.semiMonthly",
                                    ],
                                    [
                                      "monthly",
                                      "employmentIncomeFrequencyOptions.monthly",
                                    ],
                                    [
                                      "annually",
                                      "employmentIncomeFrequencyOptions.annually",
                                    ],
                                  ]
                              ).map(([value, optionKey]) => (
                                <MenuItem key={value} value={value}>
                                  {t(
                                    `affordable.application.householdIncome.${optionKey}`
                                  )}
                                </MenuItem>
                              ))}
                            </Select>
                            {jobErrors?.frequency && (
                              <FormHelperText>
                                {jobErrors.frequency.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                      <Controller
                        name={`additionalJobs.${index}.amount`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            required
                            label={
                              isCashJob
                                ? t(
                                    "affordable.application.householdIncome.cashWorkAmountLabel"
                                  )
                                : t(
                                    "affordable.application.householdIncome.additionalJobs.amountLabel"
                                  )
                            }
                            placeholder="0.00"
                            InputProps={{
                              startAdornment: <Box sx={{ mr: 0.5 }}>$</Box>,
                            }}
                            error={!!jobErrors?.amount}
                            helperText={jobErrors?.amount?.message}
                          />
                        )}
                      />
                    </Box>
                    <Controller
                      name={`additionalJobs.${index}.employerName`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label={t(
                            "affordable.application.householdIncome.cashTip.employerNameLabel"
                          )}
                          error={!!jobErrors?.employerName}
                          helperText={jobErrors?.employerName?.message}
                        />
                      )}
                    />
                    <Controller
                      name={`additionalJobs.${index}.employerAddress`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label={t(
                            "affordable.application.householdIncome.cashTip.employerAddressLabel"
                          )}
                          error={!!jobErrors?.employerAddress}
                          helperText={jobErrors?.employerAddress?.message}
                        />
                      )}
                    />
                    <Controller
                      name={`additionalJobs.${index}.employerCityStateZip`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label={t(
                            "affordable.application.householdIncome.cashTip.employerCityStateZipLabel"
                          )}
                          error={!!jobErrors?.employerCityStateZip}
                          helperText={jobErrors?.employerCityStateZip?.message}
                        />
                      )}
                    />
                    <Controller
                      name={`additionalJobs.${index}.employerWorkPhone`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label={t(
                            "affordable.application.householdIncome.cashTip.employerWorkPhoneLabel"
                          )}
                          error={!!jobErrors?.employerWorkPhone}
                          helperText={jobErrors?.employerWorkPhone?.message}
                        />
                      )}
                    />
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      }}
                    >
                      <Controller
                        name={`additionalJobs.${index}.employmentStartDate`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="date"
                            required
                            InputLabelProps={{ shrink: true }}
                            label={t(
                              "affordable.application.householdIncome.cashTip.startDateLabel"
                            )}
                            error={!!jobErrors?.employmentStartDate}
                            helperText={jobErrors?.employmentStartDate?.message}
                          />
                        )}
                      />
                      <Controller
                        name={`additionalJobs.${index}.employmentEndDate`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            label={t(
                              "affordable.application.householdIncome.cashTip.endDateLabel"
                            )}
                          />
                        )}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      }}
                    >
                      <Controller
                        name={`additionalJobs.${index}.supervisorName`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            required
                            label={t(
                              "affordable.application.householdIncome.cashTip.supervisorNameLabel"
                            )}
                            error={!!jobErrors?.supervisorName}
                            helperText={jobErrors?.supervisorName?.message}
                          />
                        )}
                      />
                      <Controller
                        name={`additionalJobs.${index}.supervisorPhone`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            required
                            label={t(
                              "affordable.application.householdIncome.cashTip.supervisorPhoneLabel"
                            )}
                            error={!!jobErrors?.supervisorPhone}
                            helperText={jobErrors?.supervisorPhone?.message}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                );
              })}

              <Button
                variant="outlined"
                onClick={() => {
                  const kind: AdditionalJobKind =
                    employmentIncome === true && cashWork === true
                      ? "cashWork"
                      : cashWork === true && employmentIncome !== true
                      ? "cashWork"
                      : "wages";
                  appendAdditionalJob(emptyAdditionalJob(kind));
                }}
              >
                {t("affordable.application.householdIncome.additionalJobs.add")}
              </Button>
            </Box>
          </Box>
        )}

        {currentGroup.groupKey === "hcv" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("affordable.application.householdIncome.hcv.intro")}
            </Typography>

            <Controller
              name="hasHcvVoucher"
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.hasHcvVoucher}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {t(
                      "affordable.application.householdIncome.hcv.hasVoucherLabel"
                    )}{" "}
                    *
                  </Typography>
                  <RadioGroup
                    row
                    value={
                      field.value === true
                        ? "yes"
                        : field.value === false
                        ? "no"
                        : ""
                    }
                    onChange={(_, val) =>
                      field.onChange(val === "yes" ? true : false)
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label={t("affordable.application.householdIncome.yes")}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label={t("affordable.application.householdIncome.no")}
                    />
                  </RadioGroup>
                  {errors.hasHcvVoucher && (
                    <FormHelperText>
                      {errors.hasHcvVoucher.message as string}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {hasHcvVoucher === true && (
              <>
                <Controller
                  name="hcvBedroomSize"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.hcvBedroomSize}>
                      <InputLabel>
                        {`${t(
                          "affordable.application.householdIncome.hcv.bedroomSizeLabel"
                        )} *`}
                      </InputLabel>
                      <Select
                        {...field}
                        label={`${t(
                          "affordable.application.householdIncome.hcv.bedroomSizeLabel"
                        )} *`}
                      >
                        <MenuItem value="0">
                          {t(
                            "affordable.application.householdIncome.hcv.bedroomSize.studio"
                          )}
                        </MenuItem>
                        <MenuItem value="1">
                          {t(
                            "affordable.application.householdIncome.hcv.bedroomSize.one"
                          )}
                        </MenuItem>
                        <MenuItem value="2">
                          {t(
                            "affordable.application.householdIncome.hcv.bedroomSize.two"
                          )}
                        </MenuItem>
                        <MenuItem value="3">
                          {t(
                            "affordable.application.householdIncome.hcv.bedroomSize.three"
                          )}
                        </MenuItem>
                        <MenuItem value="4">
                          {t(
                            "affordable.application.householdIncome.hcv.bedroomSize.four"
                          )}
                        </MenuItem>
                        <MenuItem value="5+">
                          {t(
                            "affordable.application.householdIncome.hcv.bedroomSize.fivePlus"
                          )}
                        </MenuItem>
                      </Select>
                      {errors.hcvBedroomSize && (
                        <FormHelperText>
                          {errors.hcvBedroomSize.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                <Controller
                  name="hcvIsPorting"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.hcvIsPorting}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t(
                          "affordable.application.householdIncome.hcv.isPortingLabel"
                        )}{" "}
                        *
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {t(
                          "affordable.application.householdIncome.hcv.isPortingHelper"
                        )}
                      </Typography>
                      <RadioGroup
                        row
                        value={
                          field.value === true
                            ? "yes"
                            : field.value === false
                            ? "no"
                            : ""
                        }
                        onChange={(_, val) =>
                          field.onChange(val === "yes" ? true : false)
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.yes"
                          )}
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label={t("affordable.application.householdIncome.no")}
                        />
                      </RadioGroup>
                      {errors.hcvIsPorting && (
                        <FormHelperText>
                          {errors.hcvIsPorting.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                {hcvIsPorting === true && (
                  <>
                    <Controller
                      name="hcvIssuingPha"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t(
                            "affordable.application.householdIncome.hcv.issuingPhaLabel"
                          )}
                          placeholder={
                            t(
                              "affordable.application.householdIncome.hcv.issuingPhaPlaceholder"
                            ) as string
                          }
                          helperText={t(
                            "affordable.application.householdIncome.hcv.issuingPhaHelper"
                          )}
                        />
                      )}
                    />
                    <Controller
                      name="hcvPortabilityExpires"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={`${t(
                            "affordable.application.householdIncome.hcv.expiresLabel"
                          )} *`}
                          placeholder="MM/DD/YYYY"
                          inputProps={{ inputMode: "numeric" }}
                          error={!!errors.hcvPortabilityExpires}
                          helperText={
                            errors.hcvPortabilityExpires?.message ||
                            t(
                              "affordable.application.householdIncome.hcv.expiresHelper"
                            )
                          }
                        />
                      )}
                    />
                  </>
                )}
              </>
            )}
          </Box>
        )}

        {currentGroup.groupKey === "zeroIncome" &&
          zeroIncomeMember === false && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: COLORS.yellow[50],
                border: `1px solid ${COLORS.yellow[300]}`,
              }}
            >
              <Typography variant="body2" sx={{ color: COLORS.yellow[500] }}>
                {t(
                  "affordable.application.householdIncome.zeroIncomeMemberMismatch"
                )}
              </Typography>
            </Box>
          )}

        {currentGroup.groupKey === "support" && needsRecurringGiftSource && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${COLORS.gray[100]}`,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                color="primary"
                sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}
              >
                {t(
                  "affordable.application.householdIncome.recurringGift.title"
                )}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                {t(
                  "affordable.application.householdIncome.recurringGift.helper"
                )}
              </Typography>
            </Box>
            <Controller
              name="recurringGiftSourceName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t(
                    "affordable.application.householdIncome.recurringGift.sourceNameLabel"
                  )}
                  error={!!errors.recurringGiftSourceName}
                  helperText={errors.recurringGiftSourceName?.message}
                />
              )}
            />
            <Controller
              name="recurringGiftSourceEmail"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  type="email"
                  label={t(
                    "affordable.application.householdIncome.recurringGift.sourceEmailLabel"
                  )}
                  error={!!errors.recurringGiftSourceEmail}
                  helperText={errors.recurringGiftSourceEmail?.message}
                />
              )}
            />
            <Box>
              <Controller
                name="recurringGiftSourcePhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="tel"
                    label={t(
                      "affordable.application.householdIncome.recurringGift.sourcePhoneLabel"
                    )}
                  />
                )}
              />
              <Typography
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.5,
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                {t(
                  "affordable.application.householdIncome.recurringGift.sourcePhoneHelper"
                )}
              </Typography>
            </Box>
          </Box>
        )}

        {currentGroup.groupKey === "childSupportCert" &&
          householdMinors.length > 0 && (
            <Controller
              name="childSupportChildIds"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!errors.childSupportChildIds}
                  sx={{ mt: 2 }}
                >
                  <Typography
                    color="primary"
                    sx={{
                      display: "block",
                      fontSize: 14,
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    {t(
                      "affordable.application.householdIncome.childSupportChildIdsLabel"
                    )}
                  </Typography>
                  <Select
                    multiple
                    displayEmpty
                    value={field.value ?? []}
                    onChange={(e) => {
                      const v = e.target.value;
                      field.onChange(typeof v === "string" ? v.split(",") : v);
                    }}
                    renderValue={(selected) => {
                      const ids = selected as string[];
                      if (ids.length === 0) {
                        return (
                          <Typography
                            component="span"
                            sx={{ color: "text.disabled" }}
                          >
                            {t(
                              "affordable.application.householdIncome.childSupportChildIdsHelper"
                            )}
                          </Typography>
                        );
                      }
                      return ids
                        .map((id) => {
                          const m = householdMinors.find((x) => x.id === id);
                          return m ? `${m.firstName} ${m.lastName}`.trim() : id;
                        })
                        .join(", ");
                    }}
                  >
                    {householdMinors.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {`${m.firstName} ${m.lastName}`.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.childSupportChildIds && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {(errors.childSupportChildIds as any)?.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          )}

        {currentGroup.groupKey === "childSupportCert" &&
          householdMinors.length > 0 && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              <Controller
                name="childSupportStatus"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    required
                    error={!!errors.childSupportStatus}
                  >
                    <Typography
                      color="primary"
                      sx={{
                        display: "block",
                        fontSize: 14,
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      {t(
                        "affordable.application.householdIncome.childSupportStatusLabel"
                      )}
                    </Typography>
                    <RadioGroup
                      value={field.value ?? ""}
                      onChange={(_, v) => field.onChange(v)}
                    >
                      {(["a", "b", "c", "d", "e"] as const).map((opt) => (
                        <FormControlLabel
                          key={opt}
                          value={opt}
                          control={<Radio />}
                          label={
                            t(
                              `affordable.application.householdIncome.childSupportStatusOptions.${opt}`
                            ) as string
                          }
                        />
                      ))}
                    </RadioGroup>
                    {errors.childSupportStatus && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {(errors.childSupportStatus as any)?.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              {(childSupportStatus === "a" ||
                childSupportStatus === "b" ||
                childSupportStatus === "c" ||
                childSupportStatus === "d") && (
                <Controller
                  name="childSupportAmount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      type="text"
                      label={t(
                        childSupportStatus === "c" || childSupportStatus === "d"
                          ? "affordable.application.householdIncome.childSupportAmountLabelMonthly"
                          : "affordable.application.householdIncome.childSupportAmountLabel"
                      )}
                      placeholder="0.00"
                      error={!!errors.childSupportAmount}
                      helperText={
                        (errors.childSupportAmount as any)?.message ?? ""
                      }
                      InputProps={{
                        startAdornment: (
                          <Typography
                            component="span"
                            sx={{ mr: 1, color: "text.secondary" }}
                          >
                            $
                          </Typography>
                        ),
                      }}
                    />
                  )}
                />
              )}

              {(childSupportStatus === "a" || childSupportStatus === "b") && (
                <Controller
                  name="childSupportFrequency"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      required
                      error={!!errors.childSupportFrequency}
                    >
                      <Typography
                        color="primary"
                        sx={{
                          display: "block",
                          fontSize: 14,
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        {t(
                          "affordable.application.householdIncome.childSupportFrequencyLabel"
                        )}
                      </Typography>
                      <RadioGroup
                        row
                        value={field.value ?? ""}
                        onChange={(_, v) => field.onChange(v)}
                      >
                        {(["week", "month", "annually"] as const).map((opt) => (
                          <FormControlLabel
                            key={opt}
                            value={opt}
                            control={<Radio />}
                            label={
                              t(
                                `affordable.application.householdIncome.childSupportFrequencyOptions.${opt}`
                              ) as string
                            }
                          />
                        ))}
                      </RadioGroup>
                      {errors.childSupportFrequency && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {(errors.childSupportFrequency as any)?.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              )}
            </Box>
          )}

        {currentGroup.groupKey === "spousalStatus" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Controller
              name="maritalStatus"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth required error={!!errors.maritalStatus}>
                  <InputLabel id="marital-status-label">
                    {t(
                      "affordable.application.householdIncome.spousalStatus.maritalStatusLabel"
                    )}
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="marital-status-label"
                    label={t(
                      "affordable.application.householdIncome.spousalStatus.maritalStatusLabel"
                    )}
                  >
                    {[
                      "Single",
                      "Married",
                      "Separated",
                      "Estranged",
                      "Divorced",
                      "Widowed",
                    ].map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {t(
                          `affordable.application.householdIncome.spousalStatus.options.${opt}`
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.maritalStatus && (
                    <FormHelperText>
                      {errors.maritalStatus.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {needsMaritalCertFields && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "affordable.application.householdIncome.spousalStatus.certNotice"
                  )}
                </Typography>

                <Controller
                  name="spouseName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label={t(
                        "affordable.application.householdIncome.spousalStatus.spouseNameLabel"
                      )}
                      error={!!errors.spouseName}
                      helperText={errors.spouseName?.message}
                    />
                  )}
                />

                <Controller
                  name="separationDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      label={t(
                        "affordable.application.householdIncome.spousalStatus.separationDateLabel"
                      )}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />

                {maritalStatus === "Estranged" && (
                  <Box
                    sx={{
                      border: `1px solid ${COLORS.gray[200]}`,
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Controller
                      name="estrangedConfirmed"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!errors.estrangedConfirmed}
                        >
                          <FormControlLabel
                            sx={{ alignItems: "flex-start", m: 0 }}
                            control={
                              <Checkbox
                                checked={field.value === true}
                                onChange={(e) =>
                                  field.onChange(e.target.checked)
                                }
                                sx={{ mt: -1 }}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {t(
                                  "affordable.application.householdIncome.spousalStatus.estrangedConfirmText"
                                )}
                              </Typography>
                            }
                          />
                          {errors.estrangedConfirmed && (
                            <FormHelperText>
                              {errors.estrangedConfirmed.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="estrangedInitials"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          sx={{ mt: 1.5, maxWidth: 200 }}
                          label={t(
                            "affordable.application.householdIncome.spousalStatus.estrangedInitialsLabel"
                          )}
                          inputProps={{ maxLength: 5 }}
                          error={!!errors.estrangedInitials}
                          helperText={errors.estrangedInitials?.message}
                        />
                      )}
                    />
                  </Box>
                )}

                <Controller
                  name="receivingSpousalContribution"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.receivingSpousalContribution}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t(
                          "affordable.application.householdIncome.spousalStatus.receivingContributionLabel"
                        )}{" "}
                        *
                      </Typography>
                      <RadioGroup
                        row
                        value={
                          field.value === true
                            ? "yes"
                            : field.value === false
                            ? "no"
                            : ""
                        }
                        onChange={(_, val) =>
                          field.onChange(val === "yes" ? true : false)
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.yes"
                          )}
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label={t("affordable.application.householdIncome.no")}
                        />
                      </RadioGroup>
                      {errors.receivingSpousalContribution && (
                        <FormHelperText>
                          {errors.receivingSpousalContribution.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                {receivingSpousalContribution === true && (
                  <Box>
                    <Controller
                      name="spousalContributionAmount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label={t(
                            "affordable.application.householdIncome.spousalStatus.contributionAmountLabel"
                          )}
                          placeholder="$ per month"
                          error={!!errors.spousalContributionAmount}
                          helperText={errors.spousalContributionAmount?.message}
                        />
                      )}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5, fontWeight: 400 }}
                    >
                      {t(
                        "affordable.application.householdIncome.spousalStatus.contributionAmountHelper"
                      )}
                    </Typography>
                  </Box>
                )}

                <Controller
                  name="anticipatedSpousalContribution"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      error={!!errors.anticipatedSpousalContribution}
                    >
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t(
                          "affordable.application.householdIncome.spousalStatus.anticipatedSpousalContributionLabel"
                        )}{" "}
                        *
                      </Typography>
                      <RadioGroup
                        row
                        value={
                          field.value === true
                            ? "yes"
                            : field.value === false
                            ? "no"
                            : ""
                        }
                        onChange={(_, val) =>
                          field.onChange(val === "yes" ? true : false)
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.yes"
                          )}
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label={t("affordable.application.householdIncome.no")}
                        />
                      </RadioGroup>
                      {errors.anticipatedSpousalContribution && (
                        <FormHelperText>
                          {errors.anticipatedSpousalContribution.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                <Controller
                  name="possessLegalDocs"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.possessLegalDocs}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t(
                          "affordable.application.householdIncome.spousalStatus.possessLegalDocsLabel"
                        )}{" "}
                        *
                      </Typography>
                      <RadioGroup
                        row
                        value={
                          field.value === true
                            ? "yes"
                            : field.value === false
                            ? "no"
                            : ""
                        }
                        onChange={(_, val) =>
                          field.onChange(val === "yes" ? true : false)
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.yes"
                          )}
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label={t("affordable.application.householdIncome.no")}
                        />
                      </RadioGroup>
                      {errors.possessLegalDocs && (
                        <FormHelperText>
                          {errors.possessLegalDocs.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                {possessLegalDocs === false && (
                  <Controller
                    name="noLegalDocsReason"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        multiline
                        minRows={2}
                        label={t(
                          "affordable.application.householdIncome.spousalStatus.noLegalDocsReasonLabel"
                        )}
                        error={!!errors.noLegalDocsReason}
                        helperText={errors.noLegalDocsReason?.message}
                      />
                    )}
                  />
                )}

                <Controller
                  name="legalActionsTaken"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={2}
                      label={t(
                        "affordable.application.householdIncome.spousalStatus.legalActionsLabel"
                      )}
                      placeholder={t(
                        "affordable.application.householdIncome.spousalStatus.legalActionsPlaceholder"
                      )}
                    />
                  )}
                />
              </>
            )}

            {/* Awarded-income (alimony/child support/survivor) questions —
                shown for Separated, Estranged, AND Divorced. The separation-cert
                fields above stay Separated|Estranged-only. */}
            {needsAwardedIncomeFields && (
              <>
                <Controller
                  name="awardedIncome"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.awardedIncome}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t(
                          "affordable.application.householdIncome.spousalStatus.awardedIncomeLabel"
                        )}{" "}
                        *
                      </Typography>
                      <RadioGroup
                        row
                        value={
                          field.value === true
                            ? "yes"
                            : field.value === false
                            ? "no"
                            : ""
                        }
                        onChange={(_, val) =>
                          field.onChange(val === "yes" ? true : false)
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.yes"
                          )}
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label={t("affordable.application.householdIncome.no")}
                        />
                      </RadioGroup>
                      {errors.awardedIncome && (
                        <FormHelperText>
                          {errors.awardedIncome.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                <Controller
                  name="anticipatedAwardedIncome"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.anticipatedAwardedIncome}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t(
                          "affordable.application.householdIncome.spousalStatus.anticipatedAwardedIncomeLabel"
                        )}{" "}
                        *
                      </Typography>
                      <RadioGroup
                        row
                        value={
                          field.value === true
                            ? "yes"
                            : field.value === false
                            ? "no"
                            : ""
                        }
                        onChange={(_, val) =>
                          field.onChange(val === "yes" ? true : false)
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label={t(
                            "affordable.application.householdIncome.yes"
                          )}
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label={t("affordable.application.householdIncome.no")}
                        />
                      </RadioGroup>
                      {errors.anticipatedAwardedIncome && (
                        <FormHelperText>
                          {errors.anticipatedAwardedIncome.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </>
            )}
          </Box>
        )}

        {currentGroup.groupKey === "anyOther" && otherIncomeNotListed && (
          <Controller
            name="otherIncomeDescription"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1 }}
                label={t(
                  "affordable.application.householdIncome.describeSource"
                )}
                placeholder={t(
                  "affordable.application.householdIncome.describePlaceholder"
                )}
                error={!!errors.otherIncomeDescription}
                helperText={errors.otherIncomeDescription?.message}
              />
            )}
          />
        )}

        {currentGroup.groupKey === "anyOtherNext12" &&
          anticipatedOtherIncomeNotListed && (
            <Controller
              name={"anticipatedIncome.otherIncomeDescription" as any}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={{ mt: 1 }}
                  label={t(
                    "affordable.application.householdIncome.describeSource"
                  )}
                  placeholder={t(
                    "affordable.application.householdIncome.describePlaceholder"
                  )}
                  error={
                    !!(errors as any).anticipatedIncome?.otherIncomeDescription
                  }
                  helperText={
                    (errors as any).anticipatedIncome?.otherIncomeDescription
                      ?.message
                  }
                />
              )}
            />
          )}

        {currentGroup.groupKey === "expensesPaySources" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {(
              [
                "rent",
                "utilities",
                "food",
                "clothing",
                "schoolSupplies",
                "phone",
                "tvInternet",
                "medical",
                "medications",
                "personalCare",
                "vehicle",
                "creditCards",
                "other",
                "additionalComments",
              ] as const
            ).map((key) => (
              <Controller
                key={key}
                name={`expensesPaySources.${key}` as any}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline={key === "additionalComments"}
                    minRows={key === "additionalComments" ? 2 : 1}
                    label={t(
                      `affordable.application.householdIncome.expensesPaySources.fields.${key}`
                    )}
                    placeholder={
                      t(
                        "affordable.application.householdIncome.expensesPaySources.placeholder"
                      ) as string
                    }
                  />
                )}
              />
            ))}
          </Box>
        )}
      </RegisterReviewSection>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button variant="outlined" onClick={goBack} disabled={isSaving}>
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          onClick={goNext}
          disabled={isSaving || (isLastSubStep && !isValid)}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </Box>
  );
};
